// Reykjavík, "Rainbow street to Hallgrímskirkja": a snowy winter night on Skólavörðustígur under the northern lights.
// Where we stand: on the painted rainbow of lower Skólavörðustígur, a few metres up from Laugavegur, looking south-east
// (bearing about 130°) up the street to Hallgrímskirkja on the top of Skólavörðuholt. The street climbs to the church
// square; the church's front faces us at its top, the Leifur Eiríksson statue on its granite prow before the door.
// One-point perspective: the kerbs, eaves and ridges of both sides run to VP, just over the church door.
// Back to front: deep winter sky, stars and the moon -> the northern lights (a timed display) -> Hallgrímskirkja, floodlit
// warm white (the tower, its belfry, clock and stepped spire, the wings of basalt columns stepping down on both sides) ->
// the square -> corrugated-iron houses up both sides of the street, snow on their steep roofs -> the wet street, its six
// rainbow stripes running up to the cross street -> the corner café (KAFFI, left) and the bookshop (BÆKUR, right) ->
// people -> snow at three depths.
// Cheats: the church stands about three times nearer than it does from the rainbow, as through a long lens; the rainbow
// stretch and the top of the street are pressed together. No water from here: the harbour, Harpa and the Sun Voyager
// are behind us, down the hill; the wet street stands in for it.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, glyph, streak, makePitch, gradeAd, hands } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout: the street in perspective ================= */
// A point X metres right of the street's axis and h metres above the street, at depth Z metres, is drawn at
// x = VP.x + X * s, y = VP.y + (EYE - h) * s, with s = F / Z pixels a metre. The street rises evenly, so its kerbs, the
// house fronts' feet, eaves and ridges all run to VP.
const VP = { x: 240, y: 150 };
const EYE = 3.6;                    // the eye above the street (m): a little raised, over the walkers' heads
const F = 330;                      // s = F / Z
const ROAD = 4.5, FAC = 7.5;        // half-widths (m): the painted road between the kerbs, the house fronts
const sAt = z => F / z;
const xAt = (X, s) => VP.x + X * s;
const yAt = (h, s) => VP.y + (EYE - h) * s;
const sOfY = y => (y - VP.y) / EYE;  // depth scale of the street at row y (below VP)
const RB_END = 58;                  // the rainbow stops at the cross street (Bergstaðastræti), Z 58..66
const CROSS = [58, 66];
const SQUARE = 100;                 // the houses end; the church square from here up
const GROUND_C = 158;               // the church's foot
const MOON = { x: 166, y: 38 };

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const alpha = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const get = (i, y) => { const o = (y * w + i) * 4; return [d[o], d[o + 1], d[o + 2], d[o + 3]]; };
  return { c, x, put, alpha, get, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (!MIXED[k]) { const A = hex(a), B = hex(b); MIXED[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * clamp(t, 0, 1)).toString(16).padStart(2, '0')).join(''); }
  return MIXED[k];
}
// pick from a ramp with ordered dithering (v in 0..1); only the middle of each step is dithered, so broad surfaces
// stay flat and the steps between shades are short dithered seams
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (clamp((f - k - 0.3) / 0.4, 0, 1) > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}

/* ================= Sign font ================= */
// FONT3 with the wide letters of the site's postcard (N and K four columns, M, W and V five; FONT3's read as H, П and U
// at 1x) and the Icelandic letters the signs spell
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
  'Æ': ['01111', '10100', '11110', '10100', '10111'], 'Ð': ['1110', '1001', '1101', '1001', '1110'],
  'Í': ['001', '010', '010', '010', '010'], '°': ['010', '101', '010', '000', '000'],
  0: ['111', '101', '101', '101', '111'], 1: ['010', '110', '010', '010', '111'], 2: ['111', '001', '111', '100', '111'],
  3: ['111', '001', '011', '001', '111'], 8: ['111', '101', '111', '101', '111'],
};
const FONT = { A: ['010', '101', '111', '101', '101'], B: ['110', '101', '110', '101', '110'], E: ['111', '100', '110', '100', '111'],
  F: ['111', '100', '110', '100', '100'], G: ['011', '100', '101', '101', '011'], I: ['111', '010', '010', '010', '111'],
  L: ['100', '100', '100', '100', '111'], O: ['010', '101', '101', '101', '010'], P: ['110', '101', '110', '100', '100'],
  R: ['110', '101', '110', '101', '101'], S: ['011', '100', '010', '001', '110'], T: ['111', '010', '010', '010', '010'],
  U: ['101', '101', '101', '101', '111'], D: ['110', '101', '101', '101', '110'], H: ['101', '101', '111', '101', '101'],
  C: ['011', '100', '100', '100', '011'], ' ': ['00', '00', '00', '00', '00'], '-': ['000', '000', '111', '000', '000'], ...WIDE };
const signWidth = s => [...s].reduce((w, ch) => w + (FONT[ch] || FONT[' '])[0].length + 1, 0) - 1;
function sign(c, s, x, y, col) {
  let cx = x;
  for (const ch of s) { const g = FONT[ch] || FONT[' ']; glyph(c, g, cx, y, col); cx += g[0].length + 1; }
}
// the same into a painter (prerendered layers)
function signP(p, s, x, y, col) {
  let cx = x;
  for (const ch of s) { const g = FONT[ch] || FONT[' ']; g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') p.put(cx + i, y + j, col); }); cx += g[0].length + 1; }
}

/* ================= Sky: a clear deep winter night, the town's glow low over the hill ================= */
const SKY = ['#02030f', '#030614', '#050919', '#070c1f', '#0a1026', '#0d142c', '#111933', '#161e3a', '#1c2440', '#232a46',
  '#2c3049', '#36364b', '#423c4c'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 175), 1.35) * 0.92;
    v += Math.max(0, 1 - Math.hypot((i - 240) / 260, (y - 165) / 60)) * 0.16;   // the town's glow behind the hill
    return v;
  });
}
function renderMoon() {
  const r = 6, s = r * 2 + 1, p = painter(s, s);
  const craters = [[-2, -1, 1.4], [2, 2, 1.2], [1, -3, 0.9], [-3, 2, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.3) continue;
    let col = dx + dy < -4 ? '#f8fbff' : '#e4ecf8';
    if (dd > r - 1.1) col = dx + dy > 0 ? '#aebcda' : '#d4def0';
    for (const [cx, cy, cr] of craters) if (Math.hypot(dx - cx, dy - cy) < cr) col = '#c6d2e8';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{ const r = rng(64); for (let i = 0; i < 90; i++) STARS.push({ x: Math.floor(r() * W), y: 2 + Math.floor(r() * 120), k: 12 + Math.floor(r() * 50), ph: r(), b: 0.25 + r() * 0.6, big: r() < 0.08 }); }
// two thin wisps, drifting one screen-width a loop
const WISPS = [{ y: 54, w: 70, seed: 3, k: 1, ph: 0.15 }, { y: 30, w: 46, seed: 9, k: 1, ph: 0.62 }];
function renderWisp(w, seed) {
  const h = 6, p = painter(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const u = i / (w - 1), v = Math.sin(Math.PI * u) * (1 - Math.abs(y - 2.5 - Math.sin(u * 5 + seed)) / 3) + 0.2 * Math.sin(i * 0.4 + seed);
    if (v * 0.9 > bayer(i, y) + 0.15) p.put(i, y, y < 3 ? '#2a3150' : '#222843');
  }
  return p.done();
}

/* ================= The northern lights ================= */
// A long curtain rising from behind the houses on the left, over the church and away to the upper right, and a
// fainter, higher one. Each is drawn a column at a time from strips prerendered in eight lengths: a thin bright lower
// edge, green rays fading up through teal to a violet haze, and a pink hem where the curtain burns brightest. Its line
// folds slowly; its rays come from two tables of random strengths that slide along it in opposite directions (a whole
// table a loop), so they brighten, thin and drift without ever repeating a pattern on screen.
const AUR_LEN = [40, 54, 68, 82, 96, 108, 120, 132];
function renderAuroraStrip(len) {
  const p = painter(4, len);
  for (let j = 0; j < len; j++) {
    const u = 1 - j / (len - 1);                         // 0 at the lower edge, 1 at the top
    let col, a;
    if (u < 0.05) { col = '#c8ffe0'; a = 0.9; }
    else if (u < 0.14) { col = '#6effb4'; a = 0.85; }
    else if (u < 0.5) { col = mixHex('#38e894', '#1aa888', (u - 0.14) / 0.36); a = 0.78 - (u - 0.14) * 1.1; }
    else { col = mixHex('#178080', '#4a2c8a', (u - 0.5) / 0.5); a = 0.38 * Math.pow(1 - (u - 0.5) / 0.5, 1.4); }
    for (let i = 0; i < 4; i++) {
      const q = Math.floor(a * 6 + bayer(i, j)) / 6;      // dithered alpha, six levels
      if (q > 0) p.put(i, j, col, Math.round(Math.min(1, q) * 255));
    }
  }
  return p.done();
}
const RAYS = [];                                       // two tables of ray strengths (smoothed noise, 480 long)
for (let n = 0; n < 2; n++) {
  const r = rng(70 + n), raw = Array.from({ length: W }, () => Math.pow(r(), 1.6));
  const sm = raw.map((_, i) => (raw[i] * 2 + raw[(i + 1) % W] + raw[(i + W - 1) % W]) / 4);
  RAYS.push(sm.map((_, i) => (sm[i] * 3 + sm[(i + 1) % W] + sm[(i + W - 1) % W] + sm[(i + 2) % W] * 0.5 + sm[(i + W - 2) % W] * 0.5) / 6));
}
const CURTAINS = [
  // base(x): the lower edge's row; len(x): the rays' length; x0..x1: where it shows; k: whole folds travelling a loop;
  // hi: shows only once the display passes this strength (the high arcs come out at its height)
  { x0: -20, x1: 490, base: x => 140 - 6 * Math.sin(Math.PI * clamp((x - 100) / 380, 0, 1)) + 3 * Math.sin(x * 0.05 + 0.4) + 2 * Math.sin(x * 0.12 + 1), len: x => 94 + 14 * Math.sin(x * 0.019 + 0.8), k: 2, kr: 3, a: 1.25, hi: 0 },
  { x0: 270, x1: 476, base: x => 44 - (x - 300) * 0.05 + 4 * Math.sin(x * 0.033 + 2.2) + 3 * Math.sin(x * 0.13 + 0.7), len: x => 22 + 8 * Math.sin(x * 0.05), k: 3, kr: 5, a: 0.45, hi: 0.2 },
];
// The display, the scene's big moment: a faint low arc behind the houses from 8 s, building for half a minute to full
// curtains that dance 46..100 s, fading out by 128 s; a second, softer one 156..230 s. Nothing of it at the wrap.
const AUR_ON = [[8, 46, 100, 128, 1], [156, 178, 206, 230, 0.66]];
const auroraI = t => Math.max(...AUR_ON.map(([a, b, c, d, k]) => k * smooth(a, b, t) * (1 - smooth(c, d, t))));
const AUR_COL = new Float32Array(W);              // this frame's brightness of the main curtain per column (for its light on the street)
function drawAurora(t) {
  const I = auroraI(t);
  AUR_COL.fill(0);
  if (I < 0.01) return;
  const f1 = frac(t / LOOP) * W, f2 = frac(2 * t / LOOP) * W, o1 = Math.floor(f1), o2 = Math.floor(f2), u1 = f1 - o1, u2 = f2 - o2;
  const ray0 = i => lerp(RAYS[0][i % W], RAYS[0][(i + 1) % W], u1), ray1 = i => lerp(RAYS[1][(i + W) % W], RAYS[1][(i + W - 1) % W], u2);   // the tables slide smoothly
  ctx.globalCompositeOperation = 'lighter';
  CURTAINS.forEach((C, ci) => {
    const Ic = I * clamp((I - C.hi) / (1 - C.hi) * 1.6, 0, 1);
    if (Ic < 0.01) return;
    for (let x = Math.max(0, C.x0); x < Math.min(W, C.x1); x++) {
      const edge = smooth(C.x0, C.x0 + 70, x) * (1 - smooth(C.x1 - 50, C.x1, x));
      const fold = 4 * Math.sin(TAU * (C.k * t / LOOP) + x * 0.045 + ci) + 2 * Math.sin(TAU * (C.kr * t / LOOP) - x * 0.11);
      const r0 = ray0(x + o1 + ci * 97), rays = 0.3 + 0.75 * r0 + 0.45 * ray1(x + W - o2);
      const band = 0.5 + 0.5 * Math.sin(x * 0.017 - TAU * (C.k + 1) * t / LOOP + ci * 2);   // brighter folds travelling along
      const a = Ic * C.a * edge * rays * (0.45 + 0.55 * band);
      if (ci === 0) AUR_COL[x] = a;
      if (a < 0.04) continue;
      const len = C.len(x) * (0.75 + 0.4 * band + 0.3 * RAYS[1][(x * 7) % W]), L = AUR_LEN.findIndex(v => v >= len), li = L < 0 ? AUR_LEN.length - 1 : L;
      const n = AUR_LEN[li], yb = Math.round(C.base(x) + fold + (r0 > 0.62 ? 1 : 0) - (ray1(x * 3 + o2) > 0.7 ? 1 : 0));   // a ragged hem
      ctx.globalAlpha = Math.min(1, a);
      ctx.drawImage(AUR[li], x & 3, 0, 1, n, x, yb - n + 1, 1, n);
      if (a > 0.8 && ci === 0) { ctx.globalAlpha = (a - 0.8) * 2; ctx.fillStyle = '#ff6ac0'; ctx.fillRect(x, yb + 1, 1, 1); }   // the pink hem where it burns
    }
  });
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  halo(HL.aurora, 250, 80, I * 0.9); halo(HL.aurora, 400, 70, I * 0.7); halo(HL.aurora, 90, 70, I * 0.5);
}
// Its light on the wet paint: the curtains mirrored in the street, a soft green column under each bright stretch of sky,
// the road's own colours showing through (the road's edge at column x is row 150 + 0.8 |x - 240|)
function drawAuroraStreet() {
  ctx.globalCompositeOperation = 'lighter';
  for (let x = 92; x < 388; x++) {
    const a = AUR_COL[x];
    if (a < 0.06) continue;
    const y0 = Math.max(172, Math.ceil(150 + 0.8 * Math.abs(x + 0.5 - 240)) + 1);
    if (y0 >= H) continue;
    ctx.globalAlpha = Math.min(1, a * 0.55);
    ctx.drawImage(AUR_REFL, x & 3, 0, 1, H - y0, x, y0, 1, H - y0);
  }
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
/* ================= Drifting snow ================= */
// Powder lifted by the wind and streaming low across the street and the pavements (skafrenningur), in sheets that
// cross the frame a whole number of times a loop, longer and thicker the nearer they are; and snow smoking off the near
// roofs' ridges and rakes in the same wind. On dry nights only: a wet pick lays the powder.
const DRIFT = [];
{ const r = rng(510); for (let i = 0; i < 16; i++) { const y = Math.round(176 + i * 5.8 + r() * 3), near = (y - 170) / 100; DRIFT.push({ y, L: Math.round(50 + 110 * near + r() * 30), th: 1 + Math.round(near * 2.4), k: 3 + (i % 4), ph: r(), a: 0.3 + 0.25 * near, seed: i }); } }
const PLUMES = [[60, 67, 1], [88, 98, 0.8], [425, 53, 1], [452, 80, 0.8], [372, 92, 0.7], [338, 74, 0.6]];   // [x, y, strength] where the ridges and rakes smoke
DRIFT.forEach(d => {                              // the ground at its row: the café's corner opens below row 205, the bookshop's below 208
  const fx = FAC * sOfY(d.y + d.th + 1);
  d.xl = d.y - 1 >= 205 ? 0 : Math.ceil(VP.x - fx); d.xr = d.y - 1 >= 209 ? W : Math.floor(VP.x + fx);
});
function renderDrift(L, th, seed) {
  const p = painter(L, th + 2);
  for (let j = 0; j < th + 2; j++) for (let i = 0; i < L; i++) {
    const u = i / (L - 1), v = Math.sin(Math.PI * u) * (1 - Math.abs(j - (th + 1) / 2 - 0.6 * Math.sin(u * 7 + seed)) / (th / 2 + 1.2)) * (0.65 + 0.35 * Math.sin(i * 0.37 + seed * 3));
    const q = Math.floor(v * 4 + bayer(i, j)) / 4;
    if (q > 0) p.put(i, j, '#eef4ff', Math.round(Math.min(1, q) * 255));
  }
  return p.done();
}
function renderPlume() {                           // a fan of powder off an edge, streaming right and a little down
  const w = 34, h = 12, p = painter(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const u = i / w, c = 2 + u * 5, d = Math.abs(j - c) / (1 + u * 4), v = (1 - u) * (1 - d) * (0.6 + 0.4 * Math.sin(i * 0.9 + j * 1.7));
    const q = Math.floor(v * 4 + bayer(i, j)) / 4;
    if (q > 0) p.put(i, j, '#e8eef8', Math.round(Math.min(1, q) * 220));
  }
  return p.done();
}
function drawDrift(t) {
  for (const d of DRIFT) {
    const span = W + d.L, x = Math.round(frac(d.k * t / LOOP + d.ph) * span) - d.L;
    const a = Math.max(x, d.xl), b = Math.min(x + d.L, d.xr);                // only over ground: between the fronts, or the open corners
    if (b <= a) continue;
    ctx.globalAlpha = d.a; ctx.drawImage(DRIFT_C[d.seed], a - x, 0, b - a, d.th + 2, a, d.y - 1, b - a, d.th + 2);
  }
  PLUMES.forEach(([x, y, k], i) => {                                       // gusts: each edge smokes on its own rhythm
    const g = k * (0.35 + 0.65 * Math.max(0, wave(t, 6 + i * 2, i * 0.23)));
    if (g < 0.05) return;
    ctx.globalAlpha = g * 0.45; ctx.drawImage(PLUME_C, x, y);
    ctx.fillStyle = '#f0f6ff';
    for (let j = 0; j < 7; j++) {                                          // puffs streaming off along the fan
      const u = frac(t * (40 + i * 4) / LOOP + j / 7), q = hash(i, j, 61);
      ctx.globalAlpha = g * 0.8 * (1 - u) * Math.min(1, u * 6);
      ctx.fillRect(Math.round(x + u * 32), Math.round(y + 2 + u * (4 + q * 4) + Math.sin(u * 6 + j) * 1.2), 1 + (q > 0.6 ? 1 : 0), 1);
    }
  });
  ctx.globalAlpha = 1;
}
function renderAuroraRefl() {
  const L = H - 172, p = painter(4, L);
  for (let j = 0; j < L; j++) {
    const u = j / L, a = 0.5 * Math.pow(1 - u, 0.8) + 0.12;
    for (let i = 0; i < 4; i++) { const q = Math.floor(a * 5 + bayer(i, j) * 0.999) / 5; if (q > 0) p.put(i, j, u < 0.25 ? '#40f0a8' : '#2ac890', Math.round(q * 255)); }
  }
  return p.done();
}

/* ================= Hallgrímskirkja ================= */
// Measured on a long-lens photo from Skólavörðustígur (the view drawn here): cross top to ground 7.3 tower widths; the
// cross 0.035 of that, the stepped spire 0.25, the belfry 0.105, the shaft 0.61. The first column beside the shaft
// reaches the clock (0.56 of the height); the next four step down steeply (to 0.17 within half a tower width), then the
// wings flatten into rows of low columns out to 2.6 tower widths a side (the front 0.85 of the height).
const CH = { x: 240, cross: 20, tip: 25, belfry: 60, shaft: 75, base: GROUND_C, hw: 9 };   // tower: x 231..249
const CLOCK = { x: 240, y: 81 };
const SLITS = [89, 97, 105, 114, 122, 130];
const COLS = 24;                                   // basalt columns a side, 2 px each
const colH = k => {                                // height (px above the foot) of the k-th column out from the tower, 1-based
  const steep = [0.56, 0.475, 0.375, 0.255, 0.17];
  const d = (2 * k - 1) / 19, g = k <= 5 ? steep[k - 1] : 0.09 + 0.08 * Math.exp(-(d - 0.474) / 0.3);
  return Math.round(g * (CH.base - CH.cross));
};
const COL_TOPS = Array.from({ length: COLS }, (_, i) => CH.base - colH(i + 1));
const CONC = ['#262433', '#38354a', '#4e4a5c', '#68616e', '#857b80', '#a39789', '#c0b19a', '#d9c9ad', '#ecdec2', '#f8eeda', '#fff8ea'];
const CHC = { x: 180, y: 16, w: 122, h: 148 };     // the church's canvas
const spireHW = y => Math.min(CH.hw, 1 + Math.floor((y - CH.tip) / 4.25));
const archHW = y => y < 135 ? -1 : Math.min(4, y - 135);   // the door's pointed arch, rows 135..157
// floodlit from the square: brightest at the foot, fading up the tower
const flood = y => 0.4 + 0.52 * Math.pow(clamp((y - CH.cross) / (CH.base - CH.cross), 0, 1), 0.85);
function renderChurch() {
  const p = painter(CHC.w, CHC.h), put = (x, y, col) => p.put(x - CHC.x, y - CHC.y, col);
  const shade = (x, y, v) => put(x, y, pick(CONC, v, x, y));
  const { x: cx, hw } = CH, x0 = cx - hw, x1 = cx + hw;
  // the wings: rows of basalt-like columns stepping down, a snow cap on each top
  for (let k = 1; k <= COLS; k++) for (const side of [-1, 1]) {
    const top = COL_TOPS[k - 1], a = side < 0 ? x0 - 2 * k : x1 + 2 * k - 1;   // the column's left pixel
    for (let y = top; y < CH.base; y++) {
      const v = flood(y) + (y < top + 2 ? 0.08 : 0) - (k > 5 ? 0.06 : 0);
      shade(a, y, v + 0.06);
      shade(a + 1, y, v - 0.32);
    }
    put(a, top - 1, '#dce8fa'); put(a + 1, top - 1, '#a8b8d8');                    // snow on the column's top
    if (k > 1 && COL_TOPS[k - 2] < top - 1) put(side < 0 ? a + 1 : a, top - 2, '#9aa8c8');   // a little drift against the taller one
  }
  // the shaft: flat floodlit concrete between two corner pilasters
  for (let y = CH.shaft; y < CH.base; y++) for (let x = x0; x <= x1; x++) {
    let v = flood(y);
    if (x === x0) v += 0.1; else if (x === x1) v -= 0.22;
    else if (x === x0 + 2 || x === x1 - 2) v -= 0.07;                              // the pilasters' inner edges
    shade(x, y, v);
  }
  // the belfry: three lancets lit amber from inside, louvred; a sill and a cornice under them
  for (let y = CH.belfry; y < CH.shaft; y++) for (let x = x0; x <= x1; x++) {
    let v = flood(y) - 0.02;
    if (x === x0) v += 0.1; else if (x === x1) v -= 0.22;
    shade(x, y, v);
  }
  for (const [lx, top] of [[cx - 5, 62], [cx, 60], [cx + 5, 62]]) {
    put(lx, top, '#6a5040');
    for (let y = top + 1; y <= 71; y++) for (let i = -1; i <= 1; i++) {
      const louvre = (y - top) % 2 === 0;
      put(lx + i, y, i === 1 ? (louvre ? '#9a5a24' : '#c07a34') : louvre ? '#d08a3a' : '#ffc870');
    }
    put(lx - 1, top + 1, '#e8a050'); put(lx + 1, top + 1, '#7a4a2a');
  }
  for (let x = x0; x <= x1; x++) { shade(x, 72, flood(72) + 0.12); put(x, 73, '#f2e6cc'); put(x, 74, '#5a5462'); }
  put(x0 - 1, 73, '#d9c9ad'); put(x1 + 1, 73, '#a39789');
  // the spire: tiers stepping in, each ledge lit, a honeycomb of small slots between
  for (let y = CH.tip; y < CH.belfry; y++) {
    const w = spireHW(y), tier = Math.floor((y - CH.tip) / 4.25), ledge = spireHW(y - 1) < w || y === CH.tip;
    for (let x = cx - w; x <= cx + w; x++) {
      const dx = x - cx;
      let v = flood(y) + 0.06 - Math.abs(dx) * 0.012;
      if (ledge) v += 0.16;
      else if (((x + tier) & 1) === 0 && (y - CH.tip) % 4 !== 0 && Math.abs(dx) < w) v -= 0.3;   // the slots
      if (dx === -w) v += 0.1; else if (dx === w) v -= 0.25;
      shade(x, y, v);
    }
  }
  // the cross
  for (let y = CH.cross; y < CH.tip; y++) put(cx, y, y < CH.cross + 2 ? '#fff8ea' : '#e6dcc8');
  put(cx - 1, CH.cross + 1, '#f0e6d2'); put(cx + 1, CH.cross + 1, '#c8bca8');
  // the clock: a white face with a dark rim (its hands are drawn per frame)
  for (let y = -4; y <= 4; y++) for (let x = -4; x <= 4; x++) {
    const d = Math.hypot(x, y);
    if (d < 2.7) put(CLOCK.x + x, CLOCK.y + y, d < 1.2 ? '#fffdf4' : '#f2eedf');
    else if (d < 3.6) put(CLOCK.x + x, CLOCK.y + y, '#3a3230');
  }
  // the slit windows up the shaft
  for (const y of SLITS) { put(cx, y, '#38354a'); put(cx, y + 1, '#4e4a5c'); }
  // the door: a tall pointed arch in three orders, the stained-glass lancet over the doors
  const GLASS = ['#2a5aa8', '#c8404a', '#e8b84a', '#3a8ab8', '#7a3a9a'];
  for (let y = 135; y < CH.base; y++) {
    const w = archHW(y);
    for (let dx = -w; dx <= w; dx++) {
      const x = cx + dx, a = Math.abs(dx);
      let col;
      if (a === w) col = dx < 0 ? '#fff8ea' : '#cdbfa6';
      else if (a === w - 1 && y > 136) col = dx < 0 ? '#a39789' : '#68616e';
      else if (y < 150) col = GLASS[(Math.floor((y - 138) / 2) * 3 + dx + 9) % GLASS.length];
      else col = (y === 150) ? '#5a4030' : dx === 0 ? '#5a3e2e' : '#3a2820';
      put(x, y, col);
    }
  }
  // the front terrace along the foot
  for (let x = cx - hw - 2 * COLS; x <= cx + hw + 2 * COLS; x++) { put(x, CH.base, '#8a8090'); put(x, CH.base + 1, '#5a5468'); }
  // Leifur Eiríksson on his granite prow before the door
  const PROW = [[153, 1], [154, 1], [155, 1], [156, 2], [157, 2], [158, 2], [159, 3], [160, 3]];
  for (const [y, w] of PROW) for (let dx = -w; dx <= w; dx++) put(cx + dx, y, y === 153 ? '#d8a898' : dx < 0 ? '#b88070' : dx === 0 ? '#9a6a5c' : '#74504a');
  // the portal's recess behind him in shadow (the lit glass shows only in the arch's point), so the bronze reads
  for (let y = 143; y <= 152; y++) for (let dx = -2; dx <= 2; dx++) if (Math.abs(dx) < archHW(y) - 1) put(cx + dx, y, y < 150 ? '#1c1828' : '#241a18');
  const LEIF = ['.h..', 'hHh.', 'cBcs', 'cBcs', 'cBcs', 'cbcs', '.l.s', '.l.s', 'l.l.'];
  const LK = { h: '#5a9a84', H: '#a8dcc0', c: '#3a6a5e', B: '#7ab8a0', b: '#4a8a76', l: '#4a7066', s: '#c8d4cc' };   // floodlit verdigris
  LEIF.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = LK[row[i]]; if (col) put(cx - 1 + i, 144 + j, col); } });
  return p.done();
}
// small warm lamps along the spire's ledges, each breathing on its own
const SPIRE_LAMPS = [];
for (let y = CH.tip + 4; y < CH.belfry - 2; y += 4) { const w = spireHW(y); SPIRE_LAMPS.push([CH.x - w, y], [CH.x + w, y]); }
// The bells: eight strokes 3 s apart. Each swells the belfry's light and fades; the first shakes snow off the ledges.
const BELL_T = [132, 135, 138, 141, 144, 147, 150, 153];
const strike = t => BELL_T.reduce((s, b) => s + (t >= b && t < b + 6 ? Math.exp(-(t - b) / 1.1) : 0), 0);
function drawChurch(t, clock) {
  const I = auroraI(t), sk = Math.min(1.2, strike(t));
  halo(HL.flood, CH.x, CH.base - 6, 1);
  halo(HL.tower, CH.x, 100, 0.6);
  ctx.drawImage(CHURCH_C, CHC.x, CHC.y);
  if (I > 0.02) {                                                                // the aurora's green on the concrete
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = I * 0.18; ctx.drawImage(CH_TINT, CHC.x, CHC.y);
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
  if (sk > 0.01) { ctx.globalAlpha = Math.min(1, sk * 0.85); ctx.drawImage(LANCET_C, CH.x - 6, 60); ctx.globalAlpha = 1; }
  halo(HL.belfry, CH.x, 67, 0.85 + 0.1 * wave(t, 40) + 0.8 * sk);
  halo(HL.cross, CH.x, CH.cross + 1, 0.8);
  hands(ctx, CLOCK.x, CLOCK.y, clock, 610, 2, 2, '#2a2420');
  ctx.fillStyle = '#ffe2a8';
  SPIRE_LAMPS.forEach(([x, y], i) => { ctx.globalAlpha = 0.45 + 0.4 * (0.5 + 0.5 * wave(t, 24 + (i % 5) * 6, i * 0.37)); ctx.fillRect(x, y, 1, 1); });
  const u = t - BELL_T[0];
  if (u >= 0 && u < 3) {
    ctx.fillStyle = '#e8eef8';
    SPIRE_LAMPS.forEach(([x, y], i) => {
      if (i % 3) return;
      for (let k = 0; k < 3; k++) { const v = Math.max(0, u - k * 0.25); ctx.globalAlpha = 0.85 * (1 - u / 3); ctx.fillRect(x + (x < CH.x ? -1 : 1) * (1 + (k & 1)), y + Math.round(v * v * 4) + 1, 1, 1); }
    });
  }
  ctx.globalAlpha = 1;
}
function renderLancets() {                         // the three belfry openings, lit full by the swinging bells
  const p = painter(13, 12);
  for (const [lx, top] of [[1, 2], [6, 0], [11, 2]]) for (let y = top + 1; y <= 11; y++) for (let i = -1; i <= 1; i++) p.put(lx + i, y, (y - top) % 2 === 0 ? '#ffd890' : '#fff4d8');
  return p.done();
}
function renderTint(src, col) {                    // a canvas's shape in one colour
  const [c, x] = makeCanvas(src.width, src.height);
  x.drawImage(src, 0, 0); x.globalCompositeOperation = 'source-in'; x.fillStyle = col; x.fillRect(0, 0, src.width, src.height);
  return c;
}

/* ================= The houses up both sides ================= */
// Corrugated iron in the old town's colours, white window frames, steep roofs under snow. Each house is a box along
// the street: side (-1 left, 1 right), from depth z0 to z1, eaves he and ridge hr above the street (m), D deep from its
// front, the ridge along the street. Drawn per pixel: its end wall facing us at z0 (with the gable), its front along
// the street, the roof's near slope above the eaves; far ones first, so nearer ones cover them.
const IRON = {
  red: ['#2e1016', '#4c181e', '#702628', '#963a34', '#b4523e'], yellow: ['#3a2c14', '#5c461c', '#846828', '#ac8c36', '#ccac4c'],
  teal: ['#0e2a30', '#164240', '#205e58', '#307e72', '#4c9e8c'], white: ['#2c3242', '#434c60', '#5e6a82', '#7c8aa2', '#9eaabe'],
  blue: ['#121c3c', '#1a2a58', '#243c78', '#34529a', '#4e6eb8'], green: ['#10241a', '#1a3a26', '#285636', '#3a744a', '#56945e'],
  grey: ['#24262e', '#363842', '#4a4c56', '#62646e', '#7e808a'], ochre: ['#3a1e12', '#5a2e18', '#804222', '#a85a2c', '#cc7a3c'],
  cream: ['#3e3628', '#5e523c', '#857656', '#ab9a74', '#cdbe96'], pink: ['#3a1a26', '#5a2838', '#803a4e', '#a45266', '#c47080'],
};
const ROOF = { red: '#6a2420', green: '#24503a', black: '#1c1e26', grey: '#3e404c', teal: '#1e4a4a' };
const SNOW = ['#5a6888', '#7a88a8', '#9eacc8', '#c2cee2', '#e2eaf6', '#f4f8ff'];
const HAZE = '#151a33';
const WARM = ['#ffd890', '#ffc068', '#f0a850', '#ffe6b0'];
// rows: storeys' window [bottom, top] heights (m); pitch: a window every pitch m along the front; lit: share lit
const HOUSES = [
  // left side, from the square down to the café
  { side: -1, z0: 100, z1: 124, he: 3.4, hr: 6, D: 8, fac: 17, iron: 'white', roof: 'black', rows: [[1, 2.3]], pitch: 2.6, lit: 0.5 },
  { side: -1, z0: 90, z1: 100, he: 4.4, hr: 7.2, D: 8, iron: 'yellow', roof: 'red', rows: [[1, 2.4]], pitch: 2.4, lit: 0.5 },
  { side: -1, z0: 78, z1: 90, he: 5, hr: 8, D: 8, iron: 'teal', roof: 'grey', rows: [[1, 2.4], [3.2, 4.4]], pitch: 2.4, lit: 0.45 },
  { side: -1, z0: 66, z1: 78, he: 7, hr: 10, D: 9, iron: 'red', roof: 'black', rows: [[1, 2.4], [3.4, 4.8], [5.6, 6.6]], pitch: 2.2, lit: 0.5 },
  { side: -1, z0: 48, z1: 58, he: 5, hr: 8.5, D: 9, iron: 'blue', roof: 'grey', rows: [[1, 2.4], [3.2, 4.5]], pitch: 2.3, lit: 0.5, shop: 1 },
  { side: -1, z0: 38, z1: 48, he: 7.5, hr: 11, D: 10, iron: 'cream', roof: 'red', rows: [[0.9, 2.5], [3.6, 5], [5.9, 7]], pitch: 2.2, lit: 0.5, shop: 1 },
  { side: -1, z0: 30, z1: 38, he: 5.5, hr: 9, D: 9, iron: 'green', roof: 'black', rows: [[0.9, 2.5], [3.4, 4.8]], pitch: 2.4, lit: 0.55, shop: 1 },
  { side: -1, z0: 22, z1: 30, he: 4.5, hr: 9, D: 9, iron: 'red', roof: 'black', rows: [[1.1, 3.2]], pitch: 2.6, lit: 0.8, shop: 1, corner: 'cafe' },
  // right side
  { side: 1, z0: 98, z1: 124, he: 3.6, hr: 6.2, D: 8, fac: 18, iron: 'pink', roof: 'grey', rows: [[1, 2.3]], pitch: 2.4, lit: 0.45 },
  { side: 1, z0: 86, z1: 98, he: 5.4, hr: 6, D: 10, iron: 'grey', roof: 'grey', rows: [[1, 2.4], [3.4, 4.6]], pitch: 2, lit: 0.45 },
  { side: 1, z0: 74, z1: 86, he: 4.6, hr: 7.6, D: 8, iron: 'yellow', roof: 'green', rows: [[1, 2.4]], pitch: 2.5, lit: 0.5 },
  { side: 1, z0: 66, z1: 74, he: 6, hr: 9.2, D: 8, iron: 'white', roof: 'red', rows: [[1, 2.4], [3.4, 4.8]], pitch: 2.3, lit: 0.5 },
  { side: 1, z0: 46, z1: 58, he: 6.5, hr: 9.5, D: 9, iron: 'ochre', roof: 'black', rows: [[0.9, 2.5], [3.5, 4.9]], pitch: 2.3, lit: 0.5, shop: 1 },
  { side: 1, z0: 36, z1: 46, he: 11.5, hr: 12, D: 9, iron: 'grey', roof: 'grey', rows: [[0.9, 2.5], [3.6, 4.9], [6.2, 7.5], [8.8, 10.1]], pitch: 2.1, lit: 0.45, shop: 1, wall: 1 },
  { side: 1, z0: 28, z1: 36, he: 4, hr: 7.4, D: 8, iron: 'teal', roof: 'red', rows: [[0.9, 2.5]], pitch: 2.4, lit: 0.6, shop: 1 },
  { side: 1, z0: 20.6, z1: 28, he: 5.6, hr: 9.6, D: 8, iron: 'white', roof: 'teal', rows: [[1.1, 3.1], [3.9, 5]], pitch: 2.5, lit: 0.6, shop: 1, corner: 'books' },
];
HOUSES.forEach((h, i) => { h.id = i; h.fac = h.fac || FAC; h.Xr = h.fac + h.D / 2; h.k = (h.hr - h.he) / (h.D / 2); });
// street lamps: slender poles with a lit drum on top, near the kerbs
const LAMPS = [[-5.5, 25], [5.5, 33], [-5.5, 41], [5.5, 51], [-5.5, 55], [5.5, 72]]          // none in the cross street (Z 58..66), where the car passes
  .map(([X, Z]) => { const s = sAt(Z); return { X, Z, s, x: Math.round(xAt(X, s)), foot: Math.round(yAt(0, s)), head: Math.round(yAt(4.4, s)) }; });
// light the lamps (and the corner shops) throw on fronts and ground: [x, y, rx, ry, colour, strength]
const POOLS = [
  ...LAMPS.map(l => [l.x, l.head + 4 * l.s * 0.35, 2.4 * l.s, 1.6 * l.s, '#ffb866', 0.55]),
  [26, 212, 32, 10, '#ffb060', 0.4], [100, 212, 32, 10, '#ffb060', 0.4], [63, 208, 14, 6, '#ffc070', 0.4], [404, 216, 42, 11, '#ffc878', 0.38], [452, 214, 14, 7, '#ffc878', 0.3],
];
const TINTS = {};
function tint(col, tcol, a) {
  const k = col + tcol + a;
  if (!TINTS[k]) TINTS[k] = mixHex(col, tcol, a);
  return TINTS[k];
}
function lit(i, y, col, gain = 1) {
  for (const [cx, cy, rx, ry, lc, s] of POOLS) {
    const d = Math.hypot((i - cx) / rx, (y - cy) / ry);
    if (d >= 1) continue;
    const q = Math.floor((1 - d) * s * gain * 5 + bayer(i, y)) / 5;
    if (q > 0) col = tint(col, lc, Math.min(0.6, q * 0.75));
  }
  return col;
}
const fog = z => clamp((z - 26) / 140, 0, 0.55);
// a window's glass, the same for a house, row and window index every time
// (x, y given: the pixel is noted in WREC, so a window can be switched on or off later as a whole)
function glass(hs, row, wi, x, y) {
  const h = hash(hs.id, row, wi), on = h < hs.lit;
  const col = on ? WARM[Math.floor(hash(hs.id, row, wi + 50) * WARM.length)] : hash(hs.id, row, wi + 90) < 0.3 ? '#2a3456' : '#141a2c';
  if (x !== undefined) {
    const k = hs.id + ':' + row + ':' + wi, w = WREC[k] || (WREC[k] = { hs, on, alt: on ? '#141a2c' : WARM[(hs.id + row + wi) % 3], px: [] });
    w.px.push([x, y]);
  }
  return col;
}
const WREC = {};                                   // every house window's pixels, by house:row:index
function drawHouse(p, hs) {
  const { side, z0, z1, he, hr, D, Xr, k } = hs, FAC = hs.fac, s0 = sAt(z0), s1 = sAt(z1), R = IRON[hs.iron], fz = fog((z0 + z1) / 2);
  const xa = VP.x + side * hs.fac * s1, xb = VP.x + side * (hs.fac + D) * s0;
  const xmin = Math.max(0, Math.floor(Math.min(xa, xb)) - 1), xmax = Math.min(W - 1, Math.ceil(Math.max(xa, xb)) + 1);
  const ymin = Math.max(0, Math.floor(yAt(hr + 0.4, s0)) - 1), ymax = Math.min(H - 1, Math.ceil(yAt(0, s0)));
  const iron = (v, x, y) => mixHex(pick(R, v, x, y), HAZE, fz);
  for (let y = ymin; y <= ymax; y++) for (let x = xmin; x <= xmax; x++) {
    const dx = side * (x + 0.5 - VP.x), dy = y + 0.5 - VP.y;
    let col = null;
    // 1. the end wall facing us at z0, its gable, snow along the rakes
    const Xe = dx / s0, he0 = EYE - dy / s0;
    if (Xe >= hs.fac && Xe <= hs.fac + D && he0 >= 0) {
      const top = he + k * (D / 2 - Math.abs(Xe - Xr)), snowT = 0.32;
      if (he0 <= top + snowT) {
        if (he0 > top - 0.04) col = pick(SNOW, 0.75 + (he0 - top) * 0.6, x, y);
        else if (hs.corner) col = cornerWall(hs, Xe, he0, x, y, s0);
        else col = endWall(hs, Xe, he0, x, y, s0, R);
        if (col) { p.put(x, y, mixHex(lit(x, y, col, 0.8), HAZE, fz)); continue; }
      }
    }
    // 2. the front along the street
    const sf = dx / hs.fac;
    if (sf > 0) {
      const Z = F / sf, h = EYE - dy / sf;
      if (Z >= z0 && Z <= z1 && h >= 0 && h <= he) {
        col = front(hs, Z, h, x, y, sf, R);
        p.put(x, y, mixHex(lit(x, y, col), HAZE, fz));
        continue;
      }
    }
    // 3. the roof's near slope, deep in snow, the iron showing at the eaves
    const sr = (dy + dx * k) / (EYE - he + hs.fac * k);
    if (sr > 0) {
      const Z = F / sr, X = dx / sr;
      if (Z >= z0 && Z <= z1 && X >= hs.fac && X <= Xr) {
        const u = (X - hs.fac) / (Xr - hs.fac);
        if (u < 0.08) col = mixHex(ROOF[hs.roof], '#000000', 0.15);
        else if (hs.dormer && Math.abs(Z - (z0 + z1) / 2) < 0.9 && u > 0.18 && u < 0.5) col = u < 0.24 || Math.abs(Z - (z0 + z1) / 2) > 0.7 ? '#e8e4d8' : WARM[hs.id % 4];
        else col = pick(SNOW, 0.35 + u * 0.55 + (hash(Math.floor(Z * 2), hs.id, 7) - 0.5) * 0.15, x, y);
        p.put(x, y, mixHex(col, HAZE, fz));
      }
    }
  }
  for (const k in WREC) if (WREC[k].hs === hs) WREC[k].fin = WREC[k].px.map(([x, y]) => p.get(x, y).slice(0, 3).join());
  // icicles under the eaves on the near houses
  if (z0 < 50) for (let Z = z0 + 0.3; Z < z1; Z += 0.45) {
    const s = sAt(Z), x = Math.round(xAt(side * hs.fac, s)), y = Math.round(yAt(he, s)), n = Math.floor(hash(hs.id, Math.floor(Z * 3), 3) * (s > 12 ? 4 : 2));
    for (let j = 1; j <= n; j++) p.put(x, y + j, j === n ? '#8ea4c8' : '#d8e6f8');
  }
}
// the front along the street: a stone plinth, iron, white-framed windows, a shop window on the ground floor
function front(hs, Z, h, x, y, s, R) {
  const { z0 } = hs;
  if (h < 0.45) return pick(['#2a2c36', '#3e404a', '#565864'], 0.3 + h, x, y);
  const u = Z - z0 - 0.9, wi = Math.floor(u / hs.pitch), f = u - wi * hs.pitch;
  if (hs.shop && h >= 0.6 && h <= 2.9 && wi >= 0 && Z < hs.z1 - 0.8 && (wi % 3) !== 2) {         // shop windows
    if (f < 0.12 || f > 1.9 || h < 0.72 || h > 2.78) return '#d6d2c8';
    if (h > 2.3 && h < 2.42) return '#cfccc4';
    return hash(hs.id, wi, 9) < 0.7 ? (h < 1.2 ? '#8a5a2a' : h > 2.42 ? '#f0b868' : WARM[(hs.id + wi) % 4 === 3 ? 1 : (hs.id + wi) % 3 + 1]) : '#1a2036';
  }
  for (let r = 0; r < hs.rows.length; r++) {
    const [a, b] = hs.rows[r];
    if (hs.shop && r === 0) continue;
    if (h >= a - 0.1 && h <= b + 0.1 && wi >= 0 && f <= 1.15 && Z < hs.z1 - 0.5) {
      if (f < 0.12 || f > 1.03 || h < a || h > b) return '#cfccc4';
      return glass(hs, r, wi, x, y);
    }
  }
  const rib = s > 10 && frac(Z / 0.22) < 0.5 ? -0.08 : 0;
  return pick(R, 0.55 + rib + (h / hs.he) * 0.08 + (Z - z0 < 0.25 ? 0.25 : 0), x, y);
}
// the end wall facing us: iron with ribs, windows on each storey, a small window in the gable
function endWall(hs, X, h, x, y, s, R) {
  if (h < 0.45) return '#3e404a';
  const u = X - hs.fac;
  if (hs.wall) return wallAd(hs, X, h, x, y, s);
  for (let r = 0; r < hs.rows.length; r++) {
    const [a, b] = hs.rows[r];
    for (const c of [1.2, hs.D - 2.4]) if (u >= c && u <= c + 1.2 && h >= a && h <= b) {
      if (u < c + 0.12 || u > c + 1.08 || h < a + 0.1 || h > b - 0.1) return '#d4d0c6';
      return glass(hs, r + 10, c > 2 ? 1 : 0, x, y);
    }
  }
  if (h > hs.he + 0.6 && Math.abs(X - hs.Xr) < 0.45 && h < hs.he + 1.6) return Math.abs(X - hs.Xr) < 0.3 && h > hs.he + 0.75 && h < hs.he + 1.45? glass(hs, 20, 0, x, y) : '#d4d0c6';
  const rib = s > 7 && ((Math.floor(u / 0.2) & 1) === 0) ? 0.1 : 0;
  return pick(R, 0.62 + rib - u / hs.D * 0.12, x, y);
}
// the corner houses' fronts: iron and the upper windows; their ground floors are drawn over them (renderCorners)
function cornerWall(hs, X, h, x, y, s) {
  const R = IRON[hs.iron], u = X - hs.fac;
  if (h < 0.45) return '#4a4c58';
  const rib = (Math.floor(u / 0.2) & 1) === 0 ? 0.08 : -0.04;
  return pick(R, 0.6 + rib + (h > hs.he ? 0.06 : 0), x, y);
}
// the tall block's blank end wall, where the painted sign is: grey render, a little weathered
function wallAd(hs, X, h, x, y) {
  const run = hash(Math.floor(X * 4), 31) < 0.25 ? 0.05 : 0;
  return pick(IRON.grey, 0.55 - run + (h > hs.he - 0.5 ? 0.12 : 0), x, y);
}

/* ================= The street: wet paint, kerbs, paving, the square ================= */
const RB = [                                       // the six stripes, left to right, wet under the lamps
  ['#3a0e14', '#5e161c', '#8a2226', '#b43432', '#d85048'], ['#40200c', '#683412', '#985018', '#c86e22', '#ec9038'],
  ['#3e3410', '#6a5818', '#9a8222', '#c8aa30', '#ead04a'], ['#0c2a18', '#144426', '#1e6634', '#2c8a44', '#4aae5c'],
  ['#0e1838', '#16265a', '#203a82', '#2e52aa', '#4a70cc'], ['#22102e', '#36184a', '#50226a', '#6c308c', '#8c4aac'],
];
function renderGround(p) {
  for (let y = VP.y + 4; y < H; y++) {
    const s = sOfY(y + 0.5), Z = F / s;
    for (let x = 0; x < W; x++) {
      if (y < GROUND_C + 2 && Math.abs(x - CH.x) <= CH.hw + 2 * COLS + 1) continue;                 // the church's foot stands here
      const X = (x + 0.5 - VP.x) / s, aX = Math.abs(X);
      let col;
      if (Z > SQUARE) {                                                       // the square: snow on paving
        col = pick(['#3a4462', '#56648a', '#7a88ac'], 0.45 + 0.3 * Math.max(0, 1 - Math.abs(X) / 60) + (hash(x >> 1, y, 3) - 0.5) * 0.3, x, y);
      } else if (aX <= ROAD) {
        const near = clamp((70 - Z) / 60, 0, 1), sheen = 0.08 * Math.sin(Z * 0.9 + X * 0.3);
        if (Z < RB_END) {
          const q = (X + ROAD) / 1.5, i = clamp(Math.floor(q), 0, 5), fq = q - Math.floor(q);
          const seam = (fq < 0.05 || fq > 0.95) && i > 0 && s > 4;
          let v = 0.2 + 0.3 * near + sheen + (hash(Math.floor(Z * 3), Math.floor(X * 3), 5) - 0.5) * 0.14 - Math.max(0, y - 236) / 34 * 0.14;
          if (seam) v -= 0.25;
          if (hash(Math.floor(Z * 6), Math.floor(X * 8), 6) < 0.03) v += 0.3;                         // worn paint
          col = pick(RB[i], v, x, y);
        } else if (Z < CROSS[1]) {                                                                     // the cross street
          col = pick(['#141824', '#1c2230', '#262c3c', '#323a4c'], 0.35 + sheen + (Math.abs(Z - 62) < 0.6 ? 0.4 : 0), x, y);
        } else col = pick(['#141824', '#1c2230', '#262c3c', '#323a4c'], 0.3 + sheen * 1.5 + 0.1 * near, x, y);
      } else if (aX <= ROAD + 0.22) col = aX < ROAD + 0.08 ? '#2a2e3a' : '#8a8e9e';                     // gutter, kerb
      else {                                                                                           // paving, snow at the walls' feet
        const jz = frac(Z / 1.2) < 0.05 * s / 6, jx = frac((aX - ROAD) / 1.5) < 0.04 * s / 6;
        const drift = aX > FAC - 0.35 - hash(Math.floor(Z * 2), Math.sign(X), 4) * 0.4;
        if (drift) col = pick(SNOW, 0.4 + (aX - FAC + 0.7) * 0.6, x, y);
        else col = pick(['#22252f', '#2e323e', '#3c414e', '#4c5260'], 0.45 + 0.25 * clamp((60 - Z) / 50, 0, 1) - (jz || jx ? 0.25 : 0), x, y);
        if (aX > FAC && Z < 22.5) col = pick(['#2a2e3a', '#3a3f4c', '#4a505e'], 0.5 - (jz || jx ? 0.3 : 0), x, y);   // the corners at Laugavegur
      }
      p.put(x, y, mixHex(lit(x, y, col, 1.1), HAZE, fog(Z) * 0.8));
    }
  }
}
// lamps: a slender dark-green pole, a lit drum with a cap of snow
function drawLamp(p, l) {
  const { x, foot, head, s } = l, w = Math.max(1, Math.round(s * 0.12)), hw = Math.max(2, Math.round(s * 0.28));
  for (let y = head + 1; y <= foot; y++) for (let i = 0; i < w; i++) p.put(x - (w >> 1) + i, y, i === 0 && w > 1 ? '#3a5048' : '#18241f');
  for (let i = -hw; i <= hw; i++) {
    p.put(x + i, head - 1, Math.abs(i) === hw ? '#2a3830' : '#1a2420');                       // the drum's cap
    p.put(x + i, head - 2, Math.abs(i) < hw ? '#dfe8f6' : '#9aa8c4');                         // snow on it
    p.put(x + i, head, Math.abs(i) === hw ? '#c08a4a' : '#fff0c8');                          // the lit drum
    if (hw > 2) p.put(x + i, head + 1, Math.abs(i) >= hw - 1 ? '#8a6a40' : '#ffd890');
  }
  if (s > 8) for (let i = -2; i <= 2; i++) p.put(x + i, foot, '#18241f');                      // its foot
}
// bare young trees in the pavement, strung with warm lights
const TREES = [[-6.2, 31], [-6.2, 45], [6.2, 42], [6.2, 70]].map(([X, Z], i) => {
  const s = sAt(Z); return { X, Z, s, x: Math.round(xAt(X, s)), foot: Math.round(yAt(0, s)), seed: 40 + i, bulbs: [] };
});
function drawTree(p, tr) {
  const r = rng(tr.seed), { x, foot, s } = tr, hgt = 4.6 * s, trunk = 1.6 * s;
  const BR = '#2a2224', BRL = '#4a3c38';
  for (let y = Math.round(foot - trunk); y <= foot; y++) { p.put(x, y, BR); if (s > 10) p.put(x + 1, y, BRL); }
  const branch = (bx, by, ang, len, depth) => {
    const n = Math.max(2, Math.round(len));
    for (let j = 0; j <= n; j++) {
      const px0 = bx + Math.sin(ang) * j, py0 = by - Math.cos(ang) * j;
      p.put(px0, py0, depth < 2 ? BR : BRL);
      if (depth >= 2 && j === n && r() < 0.8) tr.bulbs.push([Math.round(px0), Math.round(py0)]);
      else if (depth >= 1 && r() < 0.12) tr.bulbs.push([Math.round(px0), Math.round(py0)]);
    }
    if (depth >= 3) return;
    const ex = bx + Math.sin(ang) * n, ey = by - Math.cos(ang) * n, kids = depth === 0 ? 4 : 2;
    for (let c = 0; c < kids; c++) branch(ex, ey, ang + (c / (kids - 1 || 1) - 0.5) * (depth === 0 ? 1.6 : 1.1) + (r() - 0.5) * 0.4, len * (0.55 + r() * 0.2), depth + 1);
  };
  branch(x, foot - trunk, 0, (hgt - trunk) * 0.38, 0);
}
// Two layers: the ground and everything beyond the cross street (FAR), then everything nearer (NEAR), so the car and the
// people crossing Bergstaðastræti pass between them and go behind the corner houses.
const isFar = Z => Z >= CROSS[1];
function renderStreet() {
  TREES.forEach(tr => { tr.bulbs = []; }); for (const k in WREC) delete WREC[k]; SWITCHES.length = 0;   // (a second create() starts afresh)
  const p = painter(W, H), q = painter(W, H);
  renderGround(p);
  [...HOUSES].sort((a, b) => b.z0 - a.z0).forEach(hs => drawHouse(isFar(hs.z0) ? p : q, hs));
  [...LAMPS, ...TREES].sort((a, b) => b.Z - a.Z).forEach(o => { const d = isFar(o.Z) ? p : q; if (o.bulbs) drawTree(d, o); else drawLamp(d, o); });
  const g = painter(W, H);
  renderCafe(q, g); renderBooks(q, g); renderProps(q);
  buildSwitches(p, q);
  GLASS_C = g.done(); NEAR_C = q.done();
  return p.done();
}
// Windows that switch now and then: a dozen of the house windows, each with its own canvas of its glass in the other
// state (only the pixels still showing once lamps and trees are drawn), on for a while or off for a while each loop.
const SWITCHES = [];
function buildSwitches(p, q) {
  const sh = k => hash([...k].reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 100003, 7), 91);
  const keys = Object.keys(WREC).filter(k => WREC[k].px.length >= 5).sort((a, b) => sh(a) - sh(b));
  keys.slice(0, 16).forEach((k, i) => {
    const w = WREC[k], hs = w.hs, d = isFar(hs.z0) ? p : q, fz = fog((hs.z0 + hs.z1) / 2);
    const px = w.px.filter(([x, y], j) => d.get(x, y).slice(0, 3).join() === w.fin[j]);   // still showing as the house left it
    if (px.length < 4) return;
    const x0 = Math.min(...px.map(v => v[0])), y0 = Math.min(...px.map(v => v[1]));
    const s = painter(Math.max(...px.map(v => v[0])) - x0 + 1, Math.max(...px.map(v => v[1])) - y0 + 1);
    for (const [x, y] of px) s.put(x - x0, y - y0, mixHex(lit(x, y, w.alt), HAZE, fz));
    const a = 6 + Math.floor(hash(i, 3, 77) * 200), len = 24 + Math.floor(hash(i, 4, 77) * 60);   // switched for [a, a + len)
    SWITCHES.push({ c: s.done(), x: x0, y: y0, far: isFar(hs.z0), a, b: Math.min(234, a + len) });
  });
}
function drawSwitches(t, far) {
  for (const s of SWITCHES) if (s.far === far && t >= s.a && t < s.b) ctx.drawImage(s.c, s.x, s.y);
}
// the strings of lights on the young trees, each bulb breathing on its own
function drawBulbs(t, far) {
  ctx.fillStyle = '#ffd890';
  TREES.forEach((tr, ti) => {
    if (isFar(tr.Z) !== far) return;
    tr.bulbs.forEach(([x, y], j) => { ctx.globalAlpha = 0.55 + 0.45 * wave(t, 16 + ((j * 7 + ti) % 5) * 4, j * 0.29); ctx.fillRect(x, y, 1, 1); });
  });
  ctx.globalAlpha = 1;
}

/* ================= The corner café (left) and the bookshop (right) ================= */
// Both face us across the corner of Laugavegur. The café: red iron, its sign over two steamed windows, a half-glazed
// door, lit rooms in the gable; a bench outside with a sheepskin. The bookshop: white iron and blue trim, a window of
// shelves and a stack of new books (the Christmas book flood), a cat asleep among them, Advent candle bridges upstairs.
const CAFE = { winA: [6, 151, 41, 39], winB: [80, 151, 41, 39], door: [55, 153, 17, 47], sign: [14, 139, 99, 9], base: 204 };
const BOOKS = { win: [372, 155, 65, 43], door: [444, 158, 19, 43], sign: [378, 143, 58, 9], base: 208 };
const FRAME = '#efe4cc', FRAME_D = '#b4a88e';
// a framed window: outer [x, y, w, h], white frame 2 px, glazing bars at the given columns and rows (relative)
function frameRect(p, [x, y, w, h], bars = [], rows = [], col = FRAME) {
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const edge = i < 2 || j < 2 || i >= w - 2 || j >= h - 2, bar = bars.includes(i) || rows.includes(j);
    if (edge || bar) p.put(x + i, y + j, (i === w - 1 || j === h - 1) && edge ? FRAME_D : col);
  }
}
function snowLine(p, x0, x1, y, seed) {
  for (let x = x0; x <= x1; x++) {
    const h1 = hash(x, seed, 13);
    p.put(x, y, h1 < 0.8 ? '#e8eef8' : '#b8c6de');
    if (hash(x >> 1, seed, 14) < 0.3) p.put(x, y - 1, '#d4deee');
  }
}
function renderCafe(p, g) {
  const [ax, ay, aw, ah] = CAFE.winA, [bx, by, bw, bh] = CAFE.winB, [dx, dy, dw, dh] = CAFE.door, [sx, sy, sw, sh] = CAFE.sign;
  // the rooms behind the windows: lit ochre walls, a dark wainscot, shelves of cups, pendant lamps, tables with candles
  for (const [x0, y0, w, h] of [CAFE.winA, CAFE.winB]) {
    for (let j = 2; j < h - 2; j++) for (let i = 2; i < w - 2; i++) {
      const y = y0 + j, x = x0 + i, fl = j / h;
      let col = pick(['#7a4626', '#a8642e', '#d08a40', '#eaa850'], 0.85 - fl * 0.5 - Math.abs(i - w / 2) / w * 0.3, x, y);
      if (j > h * 0.62) col = pick(['#3a2016', '#5a301c', '#7a4424'], 0.6 - (j - h * 0.62) / h, x, y);   // the wainscot
      if (j === Math.round(h * 0.34) || j === Math.round(h * 0.34) + 1) col = j === Math.round(h * 0.34) ? '#4a2a1a' : '#6a3c22';   // a shelf
      if (j === Math.round(h * 0.34) - 1 && (i % 4 === 1 || i % 4 === 2) && hash(x, 3, 41) < 0.7) col = ['#f0e6d0', '#c84a3a', '#3a7aa8', '#e8c050'][(x >> 2) & 3];   // cups
      p.put(x, y, col);
    }
    for (const lx of [x0 + Math.round(w * 0.3), x0 + Math.round(w * 0.72)]) {                   // pendant lamps
      for (let y = y0 + 2; y < y0 + 7; y++) p.put(lx, y, '#3a2418');
      p.put(lx - 1, y0 + 7, '#2a1a12'); p.put(lx, y0 + 7, '#2a1a12'); p.put(lx + 1, y0 + 7, '#2a1a12');
      p.put(lx - 1, y0 + 8, '#ffe6a8'); p.put(lx, y0 + 8, '#fff6d8'); p.put(lx + 1, y0 + 8, '#ffe6a8');
    }
    const ty = y0 + Math.round(h * 0.72);                                                     // tables under the sill
    for (let i = 4; i < w - 4; i++) if (Math.abs(i - w / 2) > 3) { p.put(x0 + i, ty, '#2a160e'); p.put(x0 + i, ty + 1, '#4a2a18'); }
  }
  // the board over the windows, KAFFI between two cups
  for (let j = 0; j < sh; j++) for (let i = 0; i < sw; i++) {
    const edge = i === 0 || j === 0 || i === sw - 1 || j === sh - 1;
    p.put(sx + i, sy + j, edge ? (j === 0 ? '#e0cc9a' : '#8a7652') : j < 2 ? '#2c4a38' : '#1c3a2a');
  }
  const word = 'KAFFI', ww = signWidth(word), wx0 = sx + Math.round((sw - ww) / 2);
  signP(p, word, wx0, sy + 2, '#f4e2b0');
  for (const cxp of [wx0 - 10, wx0 + ww + 5]) ['.s.s.', '.....', 'wwww.', 'wwwww', '.ww..'].forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] !== '.') p.put(cxp + i, sy + 2 + j, row[i] === 'w' ? '#f4e2b0' : '#a8b4a0'); });
  snowLine(p, sx, sx + sw - 1, sy - 1, 5);
  // window frames, sills with snow, the door with its glazed top half and a step
  // the glass (on its own layer, over the people inside): steam on the lower panes, frames, sills with snow
  for (const [x0, y0, w, h] of [CAFE.winA, CAFE.winB]) for (let j = 2; j < h - 2; j++) for (let i = 2; i < w - 2; i++) {
    const fogv = (j - h * 0.45) / (h * 0.55) - (hash(x0 + i, 5, 61) < 0.12 ? 0.5 : 0);   // a few drips run clear
    if (fogv > bayer(x0 + i, y0 + j) * 0.9) g.put(x0 + i, y0 + j, '#f8e2c0', 70 + Math.round(fogv * 50));
  }
  frameRect(g, CAFE.winA, [14, 27], [14]); frameRect(g, CAFE.winB, [14, 27], [14]);
  for (const [x0, y0, w, h] of [CAFE.winA, CAFE.winB]) { for (let i = -1; i <= w; i++) { g.put(x0 + i, y0 + h, '#d8d0bc'); g.put(x0 + i, y0 + h + 1, '#6a3a30'); } snowLine(g, x0 - 1, x0 + w, y0 + h - 1, x0); }
  for (let j = 0; j < dh; j++) for (let i = 0; i < dw; i++) {
    const x = dx + i, y = dy + j, edge = i < 2 || i >= dw - 2 || j < 2;
    let col = edge ? (i >= dw - 2 ? '#3a1e16' : '#5a2e20') : j < 22 ? (i === 8 || j === 11 ? '#5a2e20' : pick(['#c07838', '#e8a050', '#ffd088'], 0.9 - j / 30, x, y)) : (j % 8 === 0 ? '#4a2418' : '#6a3424');
    if (j === 32 && i === dw - 4) col = '#e8c870';                                            // the handle
    p.put(x, y, col);
  }
  // two broad stone steps down to the pavement, wide enough for a lantern at each end
  for (let i = -6; i < dw + 6; i++) { p.put(dx + i, dy + dh, '#8a8c98'); p.put(dx + i, dy + dh + 1, '#5a5c68'); }
  for (let i = -7; i < dw + 7; i++) { p.put(dx + i, dy + dh + 2, '#9a9caa'); p.put(dx + i, dy + dh + 3, '#4a4c58'); }
  // a wall lantern by the door
  ['kkk', 'kyk', 'yYy', 'kyk', '.k.'].forEach((row, j) => { for (let i = 0; i < 3; i++) { const c = { k: '#1a1414', y: '#ffd888', Y: '#fff4d0' }[row[i]]; if (c) p.put(74 + i, 158 + j, c); } });
  // the bench under the left window: seat 6 px (0.45 m) off the pavement, a back rail, iron legs, a sheepskin
  for (let i = 8; i <= 42; i++) { p.put(i, 200, '#6a4a32'); p.put(i, 201, '#4a3222'); p.put(i, 194, '#6a4a32'); p.put(i, 195, '#4a3222'); }
  for (const lx of [10, 40]) for (let y = 194; y <= 206; y++) p.put(lx, y, y > 201 ? '#1a1418' : '#2a2024');
  for (let i = 10; i <= 22; i++) { p.put(i, 199, hash(i, 1, 44) < 0.5 ? '#f0ece4' : '#d8d0c4'); if (hash(i, 2, 44) < 0.4) p.put(i, 198, '#e8e2d8'); }
  // the gable: two lit windows (an Advent candle bridge in the left one, a curtain and a plant in the right), a round one
  for (const [wx, wy] of [[36, 104], [70, 104]]) {
    for (let j = 2; j < 20; j++) for (let i = 2; i < 13; i++) p.put(wx + i, wy + j, pick(['#a8642e', '#e09a48', '#ffd088'], 0.9 - j / 26, wx + i, wy + j));
    frameRect(p, [wx, wy, 15, 22], [7], [9]);
    for (let i = -1; i <= 15; i++) p.put(wx + i, wy + 22, '#d8d0bc');
    snowLine(p, wx - 1, wx + 15, wy + 21, wx + 3);
  }
  [[0, 3], [1, 2], [2, 1], [3, 0], [4, 1], [5, 2], [6, 3]].forEach(([i, j]) => { p.put(39 + i, 116 + j, '#fff2c0'); p.put(39 + i, 117 + j, '#6a4a2a'); });   // the candle bridge
  for (let i = 39; i <= 45; i++) p.put(i, 121, '#4a3020');
  for (let j = 2; j < 20; j++) { p.put(72, 104 + j, '#c84a4a'); p.put(73, 104 + j, j < 14 ? '#a83a3a' : '#c84a4a'); p.put(83, 104 + j, '#a83a3a'); }   // curtains
  ['..g..', '.ggg.', 'gg.gg', '.ggg.', '..k..', '.kkk.'].forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] !== '.') p.put(76 + i, 114 + j, row[i] === 'g' ? '#2a5a2a' : '#6a3a22'); });
  for (let y = -3; y <= 3; y++) for (let x = -3; x <= 3; x++) { const d = Math.hypot(x, y); if (d < 3.4) p.put(60 + x, 86 + y, d > 2.4 ? FRAME : pick(['#c07838', '#ffd088'], 0.6 - y * 0.1, x, y)); }
}
function renderBooks(p, g) {
  const [wx, wy, ww, wh] = BOOKS.win, [dx, dy, dw, dh] = BOOKS.door, [sx, sy, sw, sh] = BOOKS.sign;
  // the window: a lamp-lit room of shelves, the stack of new books, a sleeping cat
  const SPINES = ['#c83a3a', '#3a6ac8', '#e8c040', '#2a8a5a', '#e8e0d0', '#8a4ac0', '#d8783a', '#4ab0c8', '#1a1a2a'];
  for (let j = 2; j < wh - 2; j++) for (let i = 2; i < ww - 2; i++) {
    const x = wx + i, y = wy + j;
    let col = pick(['#6a4a2a', '#a8743a', '#d8a050', '#f0c070'], 0.85 - j / wh * 0.4 - Math.abs(i - 10) / ww * 0.5, x, y);
    const shelf = [13, 25, 37].find(s => j <= s && j > s - 9);
    if ([13, 25, 37].includes(j)) col = '#3a2414';
    else if (shelf !== undefined && j > shelf - 8 + Math.floor(hash(x, 7, 51) * 3)) col = mixHex(SPINES[Math.floor(hash(x, shelf, 52) * SPINES.length)], '#2a1a10', 0.18 + (hash(x, 9, 53) < 0.3 ? 0.2 : 0));
    p.put(x, y, col);
  }
  // the stack of new books, front and centre, and the cat curled on the sill board
  [[24, 6, 0], [33, 4, 3], [41, 5, 6]].forEach(([x0, n, c0]) => {
    for (let k = 0; k < n; k++) {
      const w = 7 + Math.floor(hash(x0, k, 54) * 3), off = Math.floor(hash(x0, k, 55) * 3) - 1, y = wy + wh - 4 - k * 2, col = SPINES[(c0 + k * 2) % SPINES.length];
      for (let i = 0; i < w; i++) { p.put(wx + x0 + off + i, y, i === 0 || i === w - 1 ? mixHex(col, '#000000', 0.3) : col); p.put(wx + x0 + off + i, y + 1, i < 2 ? col : '#efe4c8'); }
    }
  });
  ['.o.o......', '.ooo......', 'oeoe.dodo.', 'ooooodoooo', '.oooooooooo', 'tttttttttt.'].forEach((row, j) => { for (let i = 0; i < row.length; i++) { const c = { o: '#e89848', e: '#2a1a10', d: '#a85a24', t: '#c87830' }[row[i]]; if (c) p.put(wx + 5 + i, wy + wh - 8 + j, c); } });
  frameRect(g, BOOKS.win, [21, 43], [], '#d8dce8');
  for (let i = -1; i <= ww; i++) { g.put(wx + i, wy + wh, '#e8eaf0'); g.put(wx + i, wy + wh + 1, '#2a3a6a'); }
  snowLine(g, wx - 1, wx + ww, wy + wh - 1, 77);
  // the board: BÆKUR in gold on blue
  for (let j = 0; j < sh; j++) for (let i = 0; i < sw; i++) p.put(sx + i, sy + j, i === 0 || j === 0 || i === sw - 1 || j === sh - 1 ? '#c8a858' : j < 2 ? '#24346a' : '#1a2858');
  const word = 'BÆKUR', bw0 = signWidth(word);
  signP(p, word, sx + Math.round((sw - bw0) / 2), sy + 2, '#f0d078');
  snowLine(p, sx, sx + sw - 1, sy - 1, 9);
  // the door, glazed, a bell over it
  for (let j = 0; j < dh; j++) for (let i = 0; i < dw; i++) {
    const x = dx + i, y = dy + j, edge = i < 2 || i >= dw - 2 || j < 2 || j === 26;
    p.put(x, y, edge ? (i >= dw - 2 ? '#1a2448' : '#2a3a6a') : j < 26 ? pick(['#a8743a', '#e0a858', '#ffd890'], 0.9 - j / 30, x, y) : '#24346a');
  }
  // three stone steps down the plinth to the pavement (the wall's foot is at row 207)
  [[0, 2, 2], [2, 3, 2], [4, 4, 3]].forEach(([j0, ext, n]) => {
    for (let i = -ext; i < dw + ext; i++) for (let j = 0; j < n; j++) p.put(dx + i, dy + dh + j0 + j, j === 0 ? '#9a9caa' : j === n - 1 ? '#4a4c58' : '#6a6c78');
  });
  // upstairs: three windows, Advent candle bridges in two, a dark one with the night in it
  [[372, 120, 1], [408, 120, 0], [444, 120, 1]].forEach(([x0, y0, bridge], n) => {
    const lit = n !== 1;
    for (let j = 2; j < 19; j++) for (let i = 2; i < 16; i++) p.put(x0 + i, y0 + j, lit ? pick(['#7a4a2a', '#c0803a', '#f0b060'], 0.85 - j / 24, x0 + i, y0 + j) : j < 8 ? '#2a3456' : '#161c30');
    frameRect(p, [x0, y0, 18, 21], [8], [10], '#e4e6ee');
    if (bridge) [[0, 3], [1, 2], [2, 1], [3, 0], [4, 1], [5, 2], [6, 3]].forEach(([i, j]) => { p.put(x0 + 5 + i, y0 + 12 + j, '#fff4c8'); p.put(x0 + 5 + i, y0 + 13 + j, '#4a3020'); });
    if (bridge) for (let i = 5; i <= 11; i++) p.put(x0 + i, y0 + 17, '#3a2418');
    for (let i = -1; i <= 18; i++) p.put(x0 + i, y0 + 21, '#e8eaf0');
    snowLine(p, x0 - 1, x0 + 18, y0 + 20, x0);
  });
  // the gable's attic window and the paper star lit in it
  for (let j = 2; j < 22; j++) for (let i = 2; i < 18; i++) p.put(414 + i, 74 + j, pick(['#6a3a2a', '#b06a3a', '#e89a50'], 0.8 - Math.hypot(i - 10, j - 9) / 14, 414 + i, 74 + j));
  frameRect(p, [414, 74, 20, 24], [10], [], '#e4e6ee');
  ['...y...', '..yYy..', 'yyYYYyy', '.yYYYy.', '.yy.yy.', 'y.....y'].forEach((row, j) => { for (let i = 0; i < 7; i++) if (row[i] !== '.') p.put(420 + i, 80 + j, row[i] === 'Y' ? '#fff8d8' : '#ffd070'); });
  snowLine(p, 413, 434, 97, 21);
}

/* ================= Things on the near pavements ================= */
// shovelled snow heaped at the corners, the café's chalkboard and step lanterns, a bicycle at the bookshop
function heap(p, x0, x1, base, hgt, seed) {
  for (let x = x0; x <= x1; x++) {
    const u = (x - x0) / (x1 - x0), top = Math.round(base - hgt * Math.pow(Math.sin(Math.PI * u), 0.55) * (0.9 + 0.1 * Math.sin(u * 9 + seed)));
    for (let y = top; y <= base; y++) {
      const d = (y - top) / Math.max(1, base - top);
      p.put(x, y, y === top ? '#b8c6de' : pick(SNOW, 0.5 - d * 0.45 - u * 0.1 + (hash(x >> 1, y >> 1, seed) < 0.05 ? -0.15 : 0), x, y));
    }
  }
}
function bike(p, x, y, col) {                       // side view, rear wheel centre at (x, y)
  const wheel = cx => { for (let a = 0; a < 40; a++) { const t = a / 40 * TAU; p.put(cx + Math.round(Math.cos(t) * 5), y + Math.round(Math.sin(t) * 5), '#14141c'); } p.put(cx, y, '#8a8c98'); };
  wheel(x); wheel(x + 18);
  const ln = (x0, y0, x1, y1, c) => { const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)); for (let k = 0; k <= n; k++) p.put(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), c); };
  ln(x, y, x + 8, y, col); ln(x, y, x + 5, y - 7, col); ln(x + 8, y, x + 5, y - 7, col); ln(x + 5, y - 7, x + 14, y - 7, col);
  ln(x + 8, y, x + 14, y - 7, col); ln(x + 14, y - 7, x + 18, y, col); ln(x + 14, y - 7, x + 13, y - 10, '#2a2a34');
  ln(x + 11, y - 10, x + 15, y - 10, '#2a2a34'); ln(x + 3, y - 9, x + 7, y - 9, '#1a1a22'); p.put(x + 5, y - 8, '#2a2a34');
  ln(x + 3, y - 10, x + 7, y - 10, '#e8eef8');                                   // snow on the saddle
  ln(x + 15, y - 6, x + 19, y - 6, '#2a2a34'); ln(x + 15, y - 5, x + 19, y - 5, '#6a4a2a');   // a basket at the front
}
function renderProps(p) {
  heap(p, -4, 46, 268, 26, 3); heap(p, 30, 62, 262, 9, 5);
  heap(p, 440, 484, 268, 24, 7); heap(p, 424, 452, 266, 8, 9);
  // the café's chalkboard on the pavement: a cup drawn in chalk, steam over it
  const bx = 84, by = 203;
  for (let j = 0; j < 12; j++) for (let i = 0; i < 11; i++) {
    const edge = i === 0 || i === 10 || j === 0;
    p.put(bx + i, by + j, edge ? '#6a4a2e' : '#1e2622');
  }
  for (const lx of [bx, bx + 10]) for (let j = 12; j < 16; j++) p.put(lx + (j - 12) * (lx === bx ? -0.25 : 0.25), by + j, '#4a3220');
  ['.c.c..', 'c.c...', '......', 'ccccc.', 'c...cc', 'c...c.', '.ccc..', 'ccccc.'].forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === 'c') p.put(bx + 2 + i, by + 2 + j, '#d8dcd0'); });
  // lanterns on the café's step
  for (const lx of [50, 74]) ['.k.', 'kkk', 'kyk', 'kYk', 'kkk'].forEach((row, j) => { for (let i = 0; i < 3; i++) { const c = { k: '#1a1414', y: '#e09048', Y: '#ffd080' }[row[i]]; if (c) p.put(lx + i, 195 + j, c); } });
  // a bicycle with a basket against the bookshop's front
  bike(p, 376, 207, '#a83a3a');
}

/* ================= People ================= */
// One function draws everyone (after rome.js): a look, then a pose. Winter coats, beanies, scarves; the colours sunk
// into the night (dk) and a row of lamp light along the shoulders.
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f2d0b0', '#e2b08a', '#c48c62', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d8b878', '#a8541c', '#d4d0cc'];
const NIGHT = '#0e1228', GLOWC = '#ffd8a0';
const COATS = ['#c83a3a', '#2a3a5a', '#e8c040', '#3a6a5a', '#1c1c24', '#d87838', '#5a4a7a', '#e8e2d4', '#2a6aa8', '#7a3a2a', '#4a7a3a', '#c86a8a'];
const BOTS = ['#22283a', '#1a1a22', '#3a3a48', '#4a3a30', '#2a3446'];
/** a look: proportions for h px tall and colours dimmed for the night; o: hat (beanie), scarf, coat (rows of coat over the legs), long (hair), dk */
function look(h, skin, hair, coat, bot, o = {}) {
  const dk = o.dk ?? 0.25, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : h >= 14 ? 5 : 4, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.25), top: m(coat), topD: mixHex(coat, NIGHT, dk + 0.3),
    topL: mixHex(coat, GLOWC, 0.3), bot: m(bot), botL: mixHex(bot, '#9aa0b8', 0.4), shoe: m('#1a1418'), long: o.long || 0, coat: o.coat || 0,
    hat: o.hat ? m(o.hat) : null, scarf: o.scarf ? m(o.scarf) : null, bag: o.bag ? m(o.bag) : null };
}
/**
 * One person, feet on row fy, centred on column x. o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right,
 * -1 left); o.step: stride frame; o.sit: seated on an edge at fy. Arms o.aL / o.aR: 0 down, 1 up (pointing at the sky),
 * 2 bent up with a phone (its screen lit from behind), 3 out, 4 a cup at the chest, 5 a hand to the face.
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  if (P.h < 12) return tiny(x, fy, P, o);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1);
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, P.bot);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, P.bot); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2, st = o.step ? 1 : 0;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2 - st, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2 + st - st, P.bot);
      R(tx, fy - st, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit && view === 1) {                    // seated facing us: the legs hang down in front
    R(tx, fy + 1, 2, 5, P.bot); R(tx + bw - 2, fy + 1, 2, 5, P.bot); R(tx, fy + 6, 2, 1, P.shoe); R(tx + bw - 2, fy + 6, 2, 1, P.shoe);
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th + P.coat, P.top);                                             // the coat, long ones over the knees
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1 + P.coat, P.topD);
  R(tx, ty, bw, 1, P.topL);                                                      // lamp light on the shoulders
  if (P.scarf) R(tx + (side ? 0 : 1), ty, bw - (side ? 0 : 2), 2, P.scarf);
  if (P.bag && view === 0) R(tx + 1, ty + 2, bw - 2, th - 3, P.bag);
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { for (let k = 0; k < 4; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, k < 3 ? P.topD : P.skin); R(x + dir * 5 - (dir < 0 ? 1 : 0), ty - 5, 2, 3, '#0e1018'); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else { R(x + dir, ty + 1, 1, 3, P.topD); R(x + dir * 2, ty + 2, 2, 2, '#e8e2d4'); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 1) { R(ax, hy - 2, 1, ty - hy + 3, P.topD); R(ax, hy - 4, 1, 2, P.skin); }      // from the shoulder, up past the head
      else if (st === 2) {
        R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin);
        const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#62d8c8'); }
      } else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
      else if (st === 4) { const cy = o.sip ? hy + hh - 2 : ty + 3; R(ax, o.sip ? cy : ty + 1, 1, o.sip ? ty + 2 - cy : 3, P.topD); R(x - 1, cy, 3, 2, '#e8e2d4'); R(x - 1, cy, 3, 1, '#f8f4ec'); }
      else if (st === 6) {                                                         // a phone held high at the sky
        R(ax, hy - 3, 1, ty - hy + 4, P.topD); R(ax, hy - 4, 1, 1, P.skin);
        const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 7, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 7, 2, 1, '#ffe2a8'); R(p0, hy - 6, 2, 1, '#62d8c8'); }
      } else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2 + (hh > 4 ? 1 : 0), P.hat); R(hx + (hw >> 1), hy - 2, 1, 1, mixHex(P.hat, '#ffffff', 0.4)); }   // a beanie and its bobble
  return hy;
}
// a far figure under 12 px: a head, a three-wide coat, two legs a pixel apart (lit a little, so they read on the dark
// asphalt), a two-frame stride
function tiny(x, fy, P, o) {
  const leg = Math.max(2, Math.round(P.h * 0.38)), body = Math.max(2, Math.round(P.h * 0.34)), head = Math.max(2, P.h - leg - body);
  const ty = fy - leg - body + 1, hy = ty - head, st = o.step ? 1 : 0, view = o.view || 0;
  R(x - 1, fy - leg + 1, 1, leg - st, P.botL); R(x + 1, fy - leg + 1, 1, leg - (o.step === undefined ? 0 : 1 - st), P.botL);
  R(x - 1, ty, 3, body, P.top); R(x - 1, ty, 3, 1, P.topL);
  R(x - 1, hy, 2, head, view === 0 ? P.hair : P.skin); R(x - 1, hy, 2, 1, P.hat || P.hair);
  return hy;
}
// a small dog, facing dir; step: the other pair of legs forward; small: a far one
function dog(x, fy, dir, col, step = 0, small = false) {
  x = Math.round(x); fy = Math.round(fy);
  if (small) { R(x - 1, fy - 2, 3, 1, col); R(dir > 0 ? x + 1 : x - 2, fy - 3, 1, 1, col); R(x - 1 + step, fy - 1, 1, 1, col); R(x + 1 - step, fy - 1, 1, 1, col); return; }
  R(x - 3, fy - 3, 6, 2, col); R(dir > 0 ? x + 2 : x - 4, fy - 5, 2, 3, col); R(dir > 0 ? x + 4 : x - 5, fy - 4, 1, 1, col);
  R(x - 3 + step, fy - 1, 1, 2, col); R(x + 2 - step, fy - 1, 1, 2, col); R(dir > 0 ? x - 4 : x + 3, fy - 4, 1, 1, col);
}

/* ================= The loop's timeline ================= */
//   t (s)       what
//   8..46       the northern lights build: a faint arc low behind the houses, then the curtains (AUR_ON)
//   26..37      the café door opens; a man steps out with a cup and strolls off along Laugavegur (DOOR)
//   46..100     the display dances: the couple points, the photographer holds her phone up at the sky
//   50..62      a car crosses Bergstaðastræti left to right, its lights on the corner first (CARS)
//   64..122     a ginger street cat comes along the café front and sits by its steps; the woman on the bench holds out a hand
//   100..128    the display fades
//   132..153    the bells: eight strokes (BELL_T); snow shaken off the spire's ledges, the couple looks up
//   156..230    a second, softer display
//   178..190    a car crosses right to left
//   186..201    a woman walks along to the café and goes in (DOOR)
//   all loop    walkers up and down both pavements and across Bergstaðastræti, windows switching (SWITCHES), sips
//               of cocoa, the barista at work, someone passing an upstairs window, lights breathing, snow
// The share of the people shown by default; render(t, { crowd }) overrides it. Everyone has a fixed rank and shows while
// rank < crowd; rank 0 (the woman on the bench, the photographer, the two at the café door) shows at every level above
// 0, and crowd 0 shows nobody. The cat and the cars stay.
const CROWD = 0.6;
const PHONE_UP = [[48, 98], [176, 206]];            // the photographer's phone at the sky
const POINT = [[52, 60], [72, 80], [180, 188], [134, 140]];   // the couple: he points up
const TURN = [[16, 22], [112, 118], [214, 220]];    // they turn to each other
const CAT = { in: [64, 72], out: [112, 122], x0: -8, x: 45, fy: 206, hand: [84, 96] };
const DOOR = { open: [[26.5, 31], [196.5, 201]], out: 27.5, in: 186 };
const CARS = [{ a: 50, b: 62, dir: 1, Z: 60 }, { a: 178, b: 190, dir: -1, Z: 64 }];
const during = (t, list) => list.some(([a, b]) => t >= a && t < b);
const stride = t => Math.floor(t * 3.2) & 1;        // 768 steps a loop

// the cast who stay (feet row from the street's depth, height from it: about 1.7 m)
const hAt = fy => Math.round(1.7 * sOfY(fy));
const CAST = [
  { id: 'bench', rank: 0, x: 27, y: 200, P: look(22, 0, 3, '#3a6a8a', BOTS[0], { hat: '#c83a3a', scarf: '#e8c040', dk: 0.2 }) },
  { id: 'photo', rank: 0, x: 262, y: 200, P: look(hAt(200), 1, 0, '#e8c040', BOTS[1], { hat: '#c83a3a', dk: 0.3 }) },
  { id: 'coupleA', rank: 0.12, x: 210, y: 186, P: look(hAt(186), 0, 1, '#c83a3a', BOTS[0], { long: 3, dk: 0.35 }) },
  { id: 'coupleB', rank: 0.12, x: 216, y: 186, P: look(hAt(186) + 1, 2, 0, '#2a3a5a', BOTS[1], { hat: '#e8e2d4', dk: 0.35 }) },
];
function castPose(c, t) {
  if (c.id === 'bench') {
    const sip = frac((t + 5) / 24) < 0.13, hand = CAT_SHOWN && t >= CAT.hand[0] && t < CAT.hand[1];
    return { view: 1, sit: true, aL: 4, aR: hand ? 3 : 4, sip: sip && !hand };
  }
  if (c.id === 'photo') return { view: 0, aR: during(t, PHONE_UP) ? 6 : frac(t / 30) < 0.12 ? 4 : 2 };
  const turn = during(t, TURN);
  if (c.id === 'coupleA') return turn ? { view: 2, dir: 1 } : { view: 0 };
  return turn ? { view: 2, dir: -1 } : { view: 0, aR: during(t, POINT) ? 1 : 0 };
}
let CAT_SHOWN = false;

// People walking a route of ground points [X, Z] (metres across and up the street) at an even pace, starting at t0 of
// each period (s, divides 240); up the hill they fade into the dark of the square. Pavements end at Laugavegur (Z ~21),
// where they turn off the frame along the corner (feet on row 205-206, above the dock).
const WALKERS = [
  { id: 'downL', rank: 0.31, per: 120, t0: 94.5, v: 1.1, route: [[-6.6, 100], [-6.6, 21.4], [-24, 21.4]], spec: [1, 4, '#5a4a7a', BOTS[0], { long: 4, scarf: '#e8e2d4', coat: 2, dk: 0.3 }] },
  { id: 'upL', rank: 0.55, per: 120, t0: 50, v: 1.05, route: [[-24, 21.2], [-6.2, 21.2], [-6.2, 100]], spec: [2, 1, '#2a4a7a', '#3a3a48', { hat: '#4a7a3a', bag: '#c8a878', dk: 0.3 }] },
  { id: 'dogR', rank: 0.43, per: 120, t0: 92, v: 1.1, route: [[6.6, 100], [6.6, 21.2], [24, 21.2]], spec: [3, 0, '#1c1c24', BOTS[2], { hat: '#d87838', scarf: '#c83a3a', coat: 3, dk: 0.25 }], dog: '#e8d8b8' },
  { id: 'upR', rank: 0.72, per: 120, t0: 10, v: 1.1, route: [[24, 21.1], [6.2, 21.1], [6.2, 100]], spec: [0, 2, '#4a7a3a', BOTS[4], { long: 3, bag: '#c84a3a', dk: 0.3 }] },
  // across Bergstaðastræti, between the corner houses (drawn under the near layer, so the corners hide them)
  { id: 'xA', rank: 0.2, per: 60, t0: 12, v: 1, cross: true, route: [[-15, 61.5], [15, 61.5]], spec: [0, 5, '#d87838', '#3a4a6a', { hat: '#e8e2d4', dk: 0.3 }] },
  { id: 'xB', rank: 0.66, per: 60, t0: 25, v: 1, cross: true, route: [[15, 63.5], [-15, 63.5]], spec: [2, 0, '#3a6a5a', '#4a4a58', { dk: 0.3 }] },
];
WALKERS.forEach(w => {
  w.segs = []; let d = 0;
  for (let i = 1; i < w.route.length; i++) { const [X0, Z0] = w.route[i - 1], [X1, Z1] = w.route[i], len = Math.hypot(X1 - X0, Z1 - Z0); w.segs.push({ X0, Z0, X1, Z1, d, len }); d += len; }
  w.dur = d / w.v;
});
const LOOKS = {};
const lookFor = (w, h) => LOOKS[w.id + h] || (LOOKS[w.id + h] = look(h, ...w.spec));
/** where a walker is at t: { x, fy, s, view, dir, Z, along } or null when off its route */
function walkerAt(w, t, ahead = 0) {
  const e = ((t - w.t0) % w.per + w.per) % w.per;
  if (e >= w.dur) return null;
  const d = e * w.v + ahead, g = w.segs.find(q => d < q.d + q.len) || w.segs[w.segs.length - 1], u = clamp((d - g.d) / g.len, 0, 1);
  const X = lerp(g.X0, g.X1, u), Z = lerp(g.Z0, g.Z1, u), s = sAt(Z), dX = g.X1 - g.X0, dZ = g.Z1 - g.Z0;
  const view = Math.abs(dX) > Math.abs(dZ) ? 2 : dZ > 0 ? 0 : 1;
  return { x: xAt(X, s), fy: yAt(0, s), s, Z, view, dir: dX >= 0 ? 1 : -1 };
}

/* ================= The cars on Bergstaðastræti ================= */
// A small red hatchback with snow on its roof, 22 px long at the cross street's depth; its lights reach the corner
// before it does. Drawn between the far and near layers, so the corner houses hide it at both ends.
const CAR_ROWS = [
  '.......sssssss........',
  '......kwwwkwwwwk......',
  '.....kwwwwkwwwwwk.....',
  '..bbbbbbbbbbbbbbbbbbb.',
  '.Tbbbbbbbbbbbbbbbbbbbh',
  '.Tbbbbbbbbbbbbbbbbbbbh',
  '.dddoodddddddddoodddd.',
  '...oOOo.......oOOo....',
  '....oo.........oo.....',
];
const CAR_KEY = { s: '#dfe8f6', k: '#6a2224', w: '#7a8aac', b: '#8a2e2e', d: '#4a1c20', T: '#ff4a3a', h: '#fff2c8', o: '#121218', O: '#6a6c78' };
function drawCars(t) {
  for (const c of CARS) {
    const u = (t - c.a) / (c.b - c.a);
    if (u < -0.25 || u >= 1.05) continue;
    const s = sAt(c.Z), fy = Math.round(yAt(0, s)), x = Math.round(c.dir > 0 ? lerp(140, 340, u) : lerp(340, 140, u));
    halo(HL.beam, x + c.dir * 24, fy - 2, smooth(-0.25, -0.05, u) * (1 - smooth(0.95, 1.05, u)));   // its lights on the snow ahead
    if (u < 0 || u >= 1) continue;
    ctx.fillStyle = 'rgba(8,10,18,0.5)'; ctx.fillRect(x - 10, fy + 1, 21, 1);                        // its shadow
    ctx.drawImage(c.dir > 0 ? CAR_C[0] : CAR_C[1], x - 11, fy - 8);
    halo(HL.lampCore, x + c.dir * 11, fy - 4, 0.8); halo(HL.tail, x - c.dir * 11, fy - 4, 0.7);
  }
}

/* ================= The ginger cat ================= */
const CAT_K = { b: '#c07030', h: '#c87a36', e: '#a05a24', t: '#a05a24', l: '#7a3e16', E: '#f0e070' };
const CAT_WALK = [
  ['.......e.e', 't......hhh', '.tbbbbbbhE', '..bbbbbb..', '..l.l.l.l.'],
  ['.......e.e', 't......hhh', '.tbbbbbbhE', '..bbbbbb..', '...l.l..ll'],
];
const CAT_SIT = [
  ['...e.e', '...hhh', '...hEh', '..bbb.', '.bbbb.', '.bbbb.', 'ttbbb.'],
  ['...e.e', '...hhh', '...hEh', '..bbb.', '.bbbb.', 't.bbb.', 't.bbb.'],
];
function drawCat(t) {
  const { in: [a, b], out: [c, d], x0, x, fy } = CAT;
  if (t < a || t >= d) return;
  if (t >= b && t < c) { ctx.drawImage(CAT_C.sit[frac(t / 4) < 0.5 ? 0 : 1], x - 3, fy - 6); return; }   // sits, its tail swishing
  const going = t >= c, u = going ? (t - c) / (d - c) : (t - a) / (b - a), cx = Math.round(going ? lerp(x, x0, u) : lerp(x0, x, u));
  ctx.drawImage(CAT_C.walk[going ? 1 : 0][stride(t)], cx - 5, fy - 4);
}

/* ================= Inside: the café, the bookshop, the upstairs windows ================= */
// INSIDE: people seen through the steamed glass (drawn between the room and the glass)
const INSIDE = [
  { rank: 0.27, x: 18, y: 181, P: look(17, 0, 3, '#e8e2d4', BOTS[0], { long: 3, dk: 0.05 }), pose: t => ({ view: 2, dir: 1, sit: true, aR: 4, sip: frac(t / 20) < 0.14 }) },
  { rank: 0.58, x: 34, y: 181, P: look(17, 2, 0, '#3a6a5a', BOTS[1], { dk: 0.05 }), pose: t => ({ view: 2, dir: -1, sit: true, aR: frac(t / 8) < 0.5 && frac(t / 40) < 0.6 ? 3 : 0 }) },
  { rank: 0.38, x: 92, y: 182, P: look(17, 1, 5, '#c86a8a', BOTS[0], { dk: 0.05 }), pose: t => ({ view: 1, sit: true, aL: 4, aR: 4, sip: frac((t + 9) / 26) < 0.12 }) },
  { rank: 0.05, x: 110, y: 186, P: look(19, 3, 0, '#1c1c24', BOTS[1], { dk: 0.05 }), dx: t => Math.round(3 * wave(t, 8)), pose: t => ({ view: 1, aL: frac(t / 10) < 0.5 ? 3 : 4, aR: 0 }) },
  { rank: 0.48, x: 427, y: 194, P: look(20, 0, 2, '#4a7a3a', BOTS[0], { long: 2, dk: 0.1 }), pose: t => ({ view: 2, dir: -1, aR: frac(t / 20) < 0.3 ? 3 : 4 }) },
];
// someone passing behind an upstairs window: [window x0, y0, x1, y1 (glass), rank, times it crosses]
const PASSERS = [[38, 106, 48, 123, 0.35, [[84, 96], [204, 216]]], [446, 122, 459, 138, 0.52, [[150, 164]]]];
function drawInside(t, crowd) {
  for (const c of INSIDE) if (c.rank < crowd) person(c.x + (c.dx ? c.dx(t) : 0), c.y, c.P, c.pose(t));
  for (const [x0, y0, x1, y1, rank, list] of PASSERS) {
    if (rank >= crowd) continue;
    for (const [a, b] of list) {
      if (t < a || t >= b) continue;
      const cx = Math.round(lerp(x0 - 6, x1 + 6, (t - a) / (b - a))), hy = y1 - 13;     // a backlit head and shoulders
      const box = (x, y, w, h) => { const xa = Math.max(x, x0), xb = Math.min(x + w, x1 + 1), ya = Math.max(y, y0), yb = Math.min(y + h, y1 + 1); if (xb > xa && yb > ya) R(xa, ya, xb - xa, yb - ya, '#4a2c1c'); };
      box(cx - 2, hy, 4, 5); box(cx - 1, hy + 5, 2, 1); box(cx - 5, hy + 6, 10, 9);
    }
  }
}
// the café door: open, the lit room shows in it and spills warm light on the steps
function drawDoor(t, crowd) {
  if (crowd <= 0) return;
  const o = Math.max(...DOOR.open.map(([a, b]) => env(t, a, b, 0.7)));
  if (o < 0.02) return;
  const [dx, dy, dw, dh] = CAFE.door;
  ctx.globalAlpha = o; ctx.drawImage(DOOR_C, dx + 2, dy + 2); ctx.globalAlpha = 1;
  halo(HL.shop, dx + dw / 2, dy + dh + 2, 0.6 * o);
}
// the two at the café door: where each is at t, or null
function doorPeople(t) {
  const out = [], door = CAFE.door[0] + 8, top = CAFE.door[1] + CAFE.door[3] - 1, fy = 205;
  const m = t - DOOR.out;                                                          // he comes out, cup in hand, and strolls off
  if (m >= 0 && m < 9.5) {
    const P = DOOR_P[0];
    if (m < 1.5) out.push({ x: door, y: top, P, o: { view: 1, aR: 4 } });
    else if (m < 2.5) out.push({ x: door - 2, y: Math.round(lerp(top, fy, m - 1.5)), P, o: { view: 2, dir: -1, aR: 4, step: 1 } });
    else out.push({ x: lerp(door - 2, -12, (m - 2.5) / 7), y: fy, P, o: { view: 2, dir: -1, aR: 4, step: stride(t) } });
  }
  const n = t - DOOR.in;                                                           // she walks along and goes in
  if (n >= 0 && n < 14.5) {
    const P = DOOR_P[1];
    if (n < 10.5) out.push({ x: lerp(-12, door, n / 10.5), y: fy, P, o: { view: 2, dir: 1, step: n < 10.3 ? stride(t) : 0, aL: 0 } });
    else if (n < 12) out.push({ x: door, y: fy, P, o: { view: 0 } });
    else if (n < 13) out.push({ x: door, y: Math.round(lerp(fy, top, n - 12)), P, o: { view: 0, step: 1 } });
    else out.push({ x: door, y: top, P, o: { view: 0 }, a: 1 - (n - 13) / 1.5 });
  }
  return out;
}
const DOOR_P = [look(hAt(205), 2, 2, '#7a3a2a', BOTS[0], { hat: '#2a3a5a', scarf: '#c8a040', dk: 0.2 }), look(hAt(205) - 1, 0, 3, '#e8e2d4', BOTS[4], { long: 4, hat: '#3a6a8a', dk: 0.2 })];

/* ================= Drawing the people ================= */
function drawWalker(w, t, p) {
  const P = lookFor(w, Math.max(5, Math.round(1.7 * p.s))), st = stride(t), a = 1 - smooth(80, 100, p.Z);
  if (a < 0.02) return;
  ctx.globalAlpha = a;
  if (w.dog) {                                                                     // the dog trots ahead; the lead from hand to collar
    const q = walkerAt(w, t, 1.4), small = p.s < 8;
    if (q) {
      const ddir = p.view === 2 ? p.dir : -1, dx = q.x + (p.view === 2 ? 0 : -0.8 * q.s), dfy = q.fy + (p.view === 2 ? 0 : 1);
      if (P.h >= 12) {
        const tw = p.view === 2 ? P.tw - 2 : P.tw, ty = Math.round(p.fy) - P.ll - P.th + 1;
        const hx = p.view === 2 ? Math.round(p.x) + 4 * p.dir : Math.round(p.x) - (tw >> 1) - 4, hy = ty + 3;
        const ex = Math.round(dx) + (small ? 0 : ddir > 0 ? 1 : -2), ey = Math.round(dfy) - (small ? 3 : 4), n = Math.max(1, Math.abs(ex - hx), Math.abs(ey - hy));
        ctx.fillStyle = '#8a3030'; for (let k = 0; k <= n; k++) ctx.fillRect(Math.round(lerp(hx, ex, k / n)), Math.round(lerp(hy, ey, k / n)), 1, 1);
      }
      dog(dx, dfy, ddir, w.dog, st, small);
    }
  }
  person(p.x, p.fy, P, { view: p.view, dir: p.dir, step: st, aL: w.dog && p.view === 1 ? 3 : 0, aR: w.dog && p.view === 2 ? 3 : 0 });
  ctx.globalAlpha = 1;
}
// Bergstaðastræti: the cars and the people crossing, under the near layer
function drawCross(t, crowd) {
  drawCars(t);
  const list = [];
  for (const w of WALKERS) if (w.cross && w.rank < crowd) { const p = walkerAt(w, t); if (p) list.push([p.fy, () => drawWalker(w, t, p)]); }
  list.sort((a, b) => a[0] - b[0]).forEach(e => e[1]());
}
// Everyone out on the street and the pavements, drawn in the order of their feet
function drawStreetLife(t, crowd) {
  const DL = [], put = (y, f) => DL.push([y, f]);
  CAT_SHOWN = CAST[0].rank < crowd && t >= CAT.in[1] && t < CAT.out[0];
  for (const c of CAST) {
    if (c.rank >= crowd) continue;
    put(c.y, () => {
      const o = castPose(c, t);
      person(c.x, c.y, c.P, o);
      if (c.id !== 'bench') return;
      R(c.x - 4, c.y - 2, 9, 4, '#7a2a2a'); R(c.x - 4, c.y - 1, 9, 1, '#2a4a3a'); R(c.x - 1, c.y - 2, 1, 4, '#2a4a3a'); R(c.x - 4, c.y + 2, 9, 3, '#6a2424');   // a tartan blanket
      if (!o.sip) {                                                                // steam off her cocoa
        const ty = c.y - c.P.th + 1;
        for (let k = 0; k < 2; k++) { const u = frac(t / 3 + k / 2); ctx.globalAlpha = 0.45 * Math.sin(Math.PI * u); R(c.x + Math.round(Math.sin(u * 5 + k)), ty + 2 - Math.round(u * 6), 1, 1, '#e8eef8'); }
        ctx.globalAlpha = 1;
      }
    });
  }
  for (const w of WALKERS) if (!w.cross && w.rank < crowd) { const p = walkerAt(w, t); if (p) put(p.fy, () => drawWalker(w, t, p)); }
  if (crowd > 0) for (const d of doorPeople(t)) put(d.y, () => { ctx.globalAlpha = d.a ?? 1; person(d.x, d.y, d.P, d.o); ctx.globalAlpha = 1; });
  put(CAT.fy, () => drawCat(t));
  DL.sort((a, b) => a[0] - b[0]).forEach(e => e[1]());
}
/* @@END */

/* ================= Pre-render everything static ================= */
let VIG_C, HL, SKY_C, MOON_C, WISP_C, AUR, AUR_REFL, CHURCH_C, CH_TINT, LANCET_C, STREET_C, NEAR_C, GLASS_C, PITCH, BB_C, BB_AD, CAR_C, CAT_C, DOOR_C, DRIFT_C, PLUME_C;
function prerender() {
  HL = {
    moon: makeHalo(40, '#8fb0ff', 0.2, 1.8), moonCore: makeHalo(14, '#e0eaff', 0.3, 1.5),
    aurora: makeHalo(110, '#2aff9a', 0.07, 1.4),
    flood: makeHalo(64, '#ffe2b0', 0.07, 1.6), tower: makeHalo(60, '#ffeccc', 0.08, 1.6), belfry: makeHalo(10, '#ffb860', 0.4, 1.4),
    cross: makeHalo(6, '#fff4dc', 0.5, 1.3), lamp: makeHalo(26, '#ffb866', 0.22, 1.6), lampCore: makeHalo(7, '#ffe6b0', 0.5, 1.3),
    shop: makeHalo(54, '#ffb060', 0.12, 1.5), candle: makeHalo(6, '#ffb860', 0.45, 1.3), bbLamp: makeHalo(8, '#ffd8a0', 0.3, 1.5),
    beam: makeHalo(16, '#ffe8c0', 0.28, 1.4), tail: makeHalo(5, '#ff4a3a', 0.4, 1.3),
  };
  SKY_C = renderSky(); MOON_C = renderMoon();
  WISP_C = WISPS.map(w => renderWisp(w.w, w.seed));
  AUR = AUR_LEN.map(renderAuroraStrip); AUR_REFL = renderAuroraRefl();
  CHURCH_C = renderChurch(); CH_TINT = renderTint(CHURCH_C, '#0c4a30'); LANCET_C = renderLancets();
  STREET_C = renderStreet();
  CAR_C = [sprite(CAR_ROWS, CAR_KEY), sprite(CAR_ROWS, CAR_KEY, true)];
  CAT_C = { walk: [CAT_WALK.map(r => sprite(r, CAT_K)), CAT_WALK.map(r => sprite(r, CAT_K, true))], sit: CAT_SIT.map(r => sprite(r, CAT_K)) };
  DOOR_C = renderDoorway();
  DRIFT_C = DRIFT.map(d => renderDrift(d.L, d.th, d.seed)); PLUME_C = renderPlume();
  PITCH = makePitch(BB.w, BB.h, { accent: '#00ff9c' });
  BB_C = renderBillboard(); BB_AD = renderBillboardAd();
  VIG_C = renderVignette();
}
// the café's doorway with the door open: the lit room, a pendant lamp, the counter's edge
function renderDoorway() {
  const [, , dw, dh] = CAFE.door, w = dw - 4, h = dh - 2, p = painter(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.put(i, j, j > h - 14 ? pick(['#5a301c', '#7a4424', '#9a5a2c'], 0.7 - (j - h + 14) / 20, i, j) : pick(['#b8743a', '#e09a48', '#ffd088', '#ffe8b8'], 0.95 - j / h * 0.8 - Math.abs(i - w / 2) / w * 0.4, i, j));
  for (let j = 0; j < 6; j++) p.put(w >> 1, j, '#3a2418');
  for (let i = -1; i <= 1; i++) { p.put((w >> 1) + i, 6, '#2a1a12'); p.put((w >> 1) + i, 7, '#fff6d8'); }
  for (let i = 0; i < w; i++) { p.put(i, h - 15, '#3a2014'); p.put(i, h - 14, '#6a3c22'); }
  for (let j = 0; j < h; j++) p.put(0, j, '#2a160e');                               // the open door's edge at the hinge
  return p.done();
}
// darker corners, most at the bottom, dithered
function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const dx = (x - W / 2) / (W / 2), dy = (y - 120) / 150;
    const v = Math.max(0, Math.hypot(dx * 0.9, dy * 1.1) - 0.72) * 1.6 + Math.max(0, (y - 238) / 32) * 0.35;
    if (v > bayer(x, y) * 1.4 + 0.05) p.put(x, y, '#05060e', Math.round(Math.min(0.55, v * 0.6) * 255));
  }
  return p.done();
}

/* ================= Snow at three depths ================= */
// far flakes pass behind the church and the houses, mid ones land in the street, near ones cross in front of
// everything; the ones that pass through a lamp's light catch it
const SNOWF = [
  { count: 170, nMin: 14, nMax: 20, size: 1, col: 'rgba(170,186,222,0.55)', amp: 2, drift: -6, y1: [110, 168], lights: false },
  { count: 130, nMin: 22, nMax: 30, size: 1, col: 'rgba(222,232,250,0.85)', amp: 4, drift: -12, y1: [170, 212], lights: true },
  { count: 46, nMin: 32, nMax: 40, size: 2, col: 'rgba(240,246,255,0.92)', amp: 7, drift: -20, y1: [H + 4, H + 4], lights: true },
];
SNOWF.forEach((L, li) => {
  const r = rng(300 + li);
  L.flakes = Array.from({ length: L.count }, (_, i) => ({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), k: 20 + Math.floor(r() * 50), p: r() }));
});
const SNOW_LIGHTS = [
  ...LAMPS.filter(l => l.s > 6).map(l => [l.x, l.head + 3 * l.s * 0.4, 1.8 * l.s, 2.2 * l.s, 'rgba(255,214,150,0.95)']),
  [CAFE.door[0] + 8, 175, 60, 30, 'rgba(255,206,140,0.9)'], [404, 178, 46, 28, 'rgba(255,216,160,0.9)'],
];
function drawSnow(L, t) {
  const span = W + 40;
  ctx.fillStyle = L.col;
  for (const f of L.flakes) {
    const q = t * f.n / LOOP + f.ph, fq = Math.floor(q), cyc = ((fq % f.n) + f.n) % f.n, u = q - fq;
    const yEnd = lerp(L.y1[0], L.y1[1], hash(f.id, cyc, 2)), y = -4 + u * (yEnd + 4);
    let x = hash(f.id, cyc, 1) * span + u * L.drift + L.amp * Math.sin(TAU * (f.k * t / LOOP + f.p));
    x = ((x % span) + span) % span - 20;
    if (L.lights) {
      let col = L.col;
      for (const [cx, cy, rx, ry, lc] of SNOW_LIGHTS) { const dx = (x - cx) / rx, dy = (y - cy) / ry; if (dx * dx + dy * dy < 1) { col = lc; break; } }
      ctx.fillStyle = col;
    }
    ctx.fillRect(Math.round(x), Math.round(y), L.size, L.size);
  }
}
const WEATHER = weatherPlans(['snow']);
const wxHex = c => '#' + c.match(/\d+/g).slice(0, 3).map(v => (+v).toString(16).padStart(2, '0')).join('');
const WX = { land: [[110, 168], [170, 212], [H + 4, H + 4]], lights: SNOW_LIGHTS.map(([x, y, rx, ry, c]) => ({ x, y, rx, ry, col: wxHex(c) })) };

/* ================= Wet street: light laid on the paint ================= */
function drawWet(t) {
  streak(ctx, t, CH.x, 9, '#ffe8c0', 0.16, 3, 160, 214);                                        // the church, down the wet street
  for (const l of LAMPS) if (l.s > 5) streak(ctx, t, l.x, Math.max(1, Math.round(l.s * 0.22)), '#ffc070', 0.3, l.x, l.foot + 1, Math.min(H, l.foot + Math.round(l.s * 3.4)));
  for (const [x, w, y0] of [[26, 7, 206], [100, 7, 206], [63, 4, 205], [404, 9, 210], [452, 4, 210]]) streak(ctx, t, x, w, '#ffb868', 0.28, x * 3, y0, y0 + 26);
}

/* ================= The painted sign on the tall block's end wall ================= */
// Reykjavík paints its blank gable walls; this one carries an ad for the neighbourhood's geothermal pool: SUNDLAUG over
// steaming blue water, a swimmer in a cap, 38° in yellow. Two small lamps on arms light it from above.
const BB = { x: 316, y: 76, w: 46, h: 20 };
const BB_LAMPS = [BB.x + 9, BB.x + BB.w - 10];
const BB_NIGHT = { level: 0.8, tint: '#151a33', mix: 0.12, wear: 0.4, seed: 29 };
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h);
  const WATER = ['#0e3a5a', '#145a7a', '#1c7a98', '#2a9ab4', '#46b8c8'];
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    let col = y < 9 ? pick(['#1a2a58', '#22386a', '#2a4680'], 0.5 + y / 18, i, y) : pick(WATER, 0.9 - (y - 9) / 14 + 0.12 * Math.sin(i * 0.7 + y * 1.3), i, y);
    if (y === 9 || (y === 10 && (i & 3) === 1)) col = '#8adce8';                                // the water's surface, a little choppy
    p.put(i, y, col);
  }
  // a bather in a red cap, up to the shoulders, arms out along the edge of the pool; a ring of ripples round them
  ['...rrr...', '..rRrrr..', '..sssss..', '..sesed..', '...sssd..', '....s....', 'aaassssaa'].forEach((row, j) => {
    for (let i = 0; i < row.length; i++) { const c = { r: '#d83a3a', R: '#ff7a6a', s: '#f0c8a0', d: '#c89870', e: '#3a2a2a', a: '#e0b48c' }[row[i]]; if (c) p.put(1 + i, 3 + j, c); }
  });
  for (let i = 0; i <= 11; i++) if (i < 2 || i > 8 || (i & 1)) p.put(i, 10, '#a8e4ec');
  [[11, 8], [10, 7], [10, 6], [11, 5], [11, 4], [10, 3]].forEach(([i, j]) => p.put(i, j, '#8a9cc0'));   // a curl of steam
  signP(p, 'SUNDLAUG', 13, 2, '#f4f0e4');
  signP(p, '38°', 31, 12, '#ffd84a');
  for (let i = 14; i < 28; i += 3) { p.put(i, 14, '#a8e4ec'); p.put(i + 1, 13, '#a8e4ec'); }        // ripples
  return gradeAd(p.done(), BB_NIGHT);
}
function renderBillboard() {
  // the lamps' arms off the wall and a thin painted border round the face
  const p = painter(BB.w + 8, BB.h + 10), put = (x, y, c) => p.put(x - BB.x + 4, y - BB.y + 8, c);
  for (let i = -1; i <= BB.w; i++) { put(BB.x + i, BB.y - 1, '#d8d4c8'); put(BB.x + i, BB.y + BB.h, '#8a8a90'); }
  for (let j = -1; j <= BB.h; j++) { put(BB.x - 1, BB.y + j, '#c8c4b8'); put(BB.x + BB.w, BB.y + j, '#7a7a84'); }
  for (const lx of BB_LAMPS) {
    for (let j = 2; j <= 6; j++) put(lx, BB.y - j, '#2a2c34');
    put(lx - 1, BB.y - 6, '#2a2c34'); put(lx - 2, BB.y - 6, '#3a3c44'); put(lx + 1, BB.y - 6, '#2a2c34');
    put(lx - 1, BB.y - 7, '#dce6f6'); put(lx, BB.y - 7, '#dce6f6'); put(lx + 1, BB.y - 7, '#b8c6de');
  }
  return p.done();
}
function drawBillboard(t, pitch) {
  ctx.drawImage(BB_C, BB.x - 4, BB.y - 8);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(BB_AD, BB.x, BB.y);
    const sw = cycle(t, 48);                                                                   // steam drifting off the water, every 5 s
    ctx.fillStyle = '#d8e6f4';
    for (let k = 0; k < 3; k++) { const u = frac(sw.u + k / 3); ctx.globalAlpha = 0.5 * Math.sin(Math.PI * u); ctx.fillRect(BB.x + 11 + Math.round(Math.sin(u * 6 + k)), BB.y + 8 - Math.round(u * 7), 1, 1); }
    ctx.globalAlpha = 1;
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { px(ctx, lx - 1, BB.y - 5, '#ffe2b0'); px(ctx, lx, BB.y - 5, '#fff2d0'); halo(HL.bbLamp, lx, BB.y - 3, 0.9); }
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) or 'job' (the website's) on the painted sign; billboard: false leaves
// the sign, its lamps and their light out (the loop videos); weather: a WEATHERS pick, the city's own (snow) when left
// out; clock: the city's time in minutes for the church clock; crowd: the share of the people to show (CROWD when left
// out or not a number), 0 = nobody
function renderFrame(t, { pitch = false, billboard = true, weather, clock, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather);
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  // sky, stars, moon, wisps, the northern lights
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e6eeff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); if (s.big) { ctx.globalAlpha *= 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); } }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - 6, MOON.y - 6);
  WISPS.forEach((w, i) => { const x = Math.round(frac(w.k * t / LOOP + w.ph) * (W + w.w)) - w.w; ctx.globalAlpha = 0.7; ctx.drawImage(WISP_C[i], x, w.y); });
  ctx.globalAlpha = 1;
  drawAurora(t);
  if (wx.snow) drawSnow(SNOWF[0], t);
  drawWeather(ctx, wx.shared, 0, t, WX, reducedMotion);
  // the church; the street beyond the cross street; Bergstaðastræti's cars and walkers; the near street and houses
  drawChurch(t, clock);
  ctx.drawImage(STREET_C, 0, 0);
  drawSwitches(t, true); drawBulbs(t, true);
  drawCross(t, crowd);
  ctx.drawImage(NEAR_C, 0, 0);
  drawSwitches(t, false); drawBulbs(t, false);
  for (const l of LAMPS) { halo(HL.lamp, l.x, l.head + 1, Math.min(1, l.s / 10)); halo(HL.lampCore, l.x, l.head, Math.min(1, l.s / 8)); }
  drawWet(t); drawAuroraStreet();
  const dry = !wx.shared || wx.shared === 'snow' || wx.shared === 'leaves';
  if (dry) drawDrift(t);
  if (billboard) drawBillboard(t, pitch);
  // the people inside, the glass, the open door, then the people out in the street
  drawInside(t, crowd);
  ctx.drawImage(GLASS_C, 0, 0);
  drawDoor(t, crowd);
  halo(HL.shop, 60, 172, 0.9); halo(HL.shop, 404, 176, 0.75);
  if (wx.snow) drawSnow(SNOWF[1], t);
  drawWeather(ctx, wx.shared, 1, t, WX, reducedMotion);
  drawStreetLife(t, crowd);
  ctx.drawImage(VIG_C, 0, 0);
  for (const [lx, n] of [[51, 0], [75, 1]]) halo(HL.candle, lx, 197, 0.75 + 0.25 * wave(t, 300 + n * 37, n * 0.3));
  if (wx.snow) drawSnow(SNOWF[2], t);
  drawWeather(ctx, wx.shared, 2, t, WX, reducedMotion);
}

export default {
  id: 'reykjavik',
  name: 'Reykjavík',
  tagline: 'Aurora over the rainbow street',
  timeZone: 'Atlantic/Reykjavik',
  accent: '#00ff9c',
  focusX: CH.x,
  landmark: [CH.x - CH.hw - 2 * COLS, CH.x + CH.hw + 2 * COLS],   // the church with both stepped wings
  landmarkTop: CH.cross,
  billboard: BB,
  crowd: CROWD,                                    // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  spots: [
    { kind: 'landmark', x: CH.x - 10, y: CH.cross, w: 21, h: CH.belfry - 5 - CH.cross },   // the stepped spire and its cross
    { kind: 'bell', x: CH.x - 10, y: CH.belfry - 5, w: 21, h: CH.shaft + 2 - CH.belfry + 5 },   // the belfry, a finger tall
    { kind: 'window', x: 70, y: 104, w: 15, h: 22 },                                    // the café's gable window with the plant
  ],
  ambience: { wind: 0.3, crowd: 0.06, traffic: 0.03 },
  events: [
    ...BELL_T.map((t, i) => ({ t, type: 'bell', x: CH.x, gain: i === 0 ? 0.36 : 0.28 })),
    { t: DOOR.open[0][0] + 0.4, type: 'chime', x: 63, gain: 0.12 }, { t: DOOR.open[1][0] + 0.4, type: 'chime', x: 63, gain: 0.12 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
